import { jsxs, jsx, Fragment } from '@emotion/react/jsx-runtime';
import { css, keyframes, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import isNil from 'lodash/isNil';
import { forwardRef, useState, useImperativeHandle, useRef, useEffect, useContext, createContext, Fragment as Fragment$1 } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import find from 'lodash/find';
import { debounce, isEmpty, isNil as isNil$1, filter, findIndex, cloneDeep, toNumber } from 'lodash';
import { cssTransition, toast, ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

/**
 * 버튼
 *
 * @component
 *
 * @example
 * <ExButton variant="contained" size="large" label="버튼" onClick={() => {}} />
 *
 * @constructor
 */
const ExButton = ({ 
/** @param variant - ghkdgd */
variant, size, label, backgroundColor, borderColor, labelColor, icon, iconPosition = 'left', iconColor, fileLabel, extension, width, onClick, disabled = false, className, }) => {
    return (jsxs(Button, Object.assign({}, (className && { className }), { role: "button", variant: variant, size: size, label: label, backgroundColor: backgroundColor, borderColor: borderColor, labelColor: labelColor, icon: icon, iconPosition: iconPosition, iconColor: iconColor, width: width, disabled: disabled, onClick: (event) => {
            if (!disabled) {
                onClick && onClick(event);
            }
        } }, { children: [!isNil(icon) && iconPosition === 'left' && (jsx("span", Object.assign({ className: "xicon-" }, { children: icon }))), !isNil(label) && label !== '' && jsx("label", { children: label }), !isNil(fileLabel) && fileLabel !== '' && (jsxs("label", Object.assign({ className: "file_button_design" }, { children: [jsx("span", Object.assign({ className: "ellipsis" }, { children: fileLabel })), jsx("span", Object.assign({ className: "grow-1" }, { children: extension }))] }))), !isNil(icon) && iconPosition === 'right' && (jsx("span", Object.assign({ className: "xicon-" }, { children: icon })))] })));
};
const Button = styled.div `
	${props => !isNil(props.width) && { width: `${props.width}px` }};
	height: ${props => props.size === 'tiny'
    ? '32px'
    : props.size === 'small' || props.size === 'medium'
        ? '36px'
        : props.size === 'large'
            ? '44px'
            : '60px'};
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	background-color: ${props => props.variant === 'contained'
    ? props.backgroundColor || props.theme.color.primary[60]
    : props.backgroundColor || props.theme.color.white};

	//bordered border
	${props => props.variant === 'bordered' && {
    border: `1px solid ${props.borderColor || props.theme.color.primary[60]}`,
}};

	//label (default)
	padding: ${props => props.size === 'tiny'
    ? `6px ${(!isNil(props.icon) && props.iconPosition === 'left'
        ? 4
        : 0) + 8}px 6px ${(!isNil(props.icon) && props.iconPosition === 'right'
        ? 4
        : 0) + 8}px`
    : props.size === 'small' ||
        props.size === 'medium' ||
        props.size === 'large'
        ? `8px ${16 -
            (!isNil(props.icon) && props.iconPosition === 'right'
                ? 4
                : 0)}px 8px ${16 -
            (!isNil(props.icon) && props.iconPosition === 'left'
                ? 4
                : 0)}px`
        : `16px ${16 -
            (!isNil(props.icon) && props.iconPosition === 'right'
                ? 4
                : 0)}px 16px ${16 -
            (!isNil(props.icon) && props.iconPosition === 'left'
                ? 4
                : 0)}px`};
	//icon only
	${props => !isNil(props.icon) &&
    isNil(props.label) &&
    (props.size === 'tiny'
        ? { padding: '6px' }
        : props.size === 'small' || props.size === 'medium'
            ? { padding: '8px' }
            : props.size === 'large'
                ? { padding: '10px' }
                : { padding: '18px' })};
	cursor: pointer;
	label {
		cursor: inherit;
		user-select: none;
		white-space: nowrap;
		color: ${props => !isNil(props.labelColor)
    ? props.labelColor
    : props.variant === 'contained'
        ? props.theme.color.white
        : props.theme.color.primary[60]};
		${props => props.size === 'tiny'
    ? props.theme.font.Button_s
    : props.size === 'small'
        ? props.theme.font.Button_s
        : props.size === 'medium'
            ? props.theme.font.Button_m
            : props.size === 'large'
                ? props.theme.font.Button_l
                : props.size === 'xLarge'
                    ? props.theme.font.Button_xl
                    : ''};
	}
	.xicon- {
		user-select: none;
		color: ${props => !isNil(props.iconColor)
    ? props.iconColor
    : props.variant === 'contained'
        ? props.theme.color.white
        : props.theme.color.primary[60]};
		font-size: ${props => props.size === 'large' || props.size === 'xLarge'
    ? '24px'
    : '20px'};
		line-height: ${props => props.size === 'large' || props.size === 'xLarge'
    ? '24px'
    : '20px'};
		${({ disabled, theme }) => disabled && `color: ${theme.color.mono[10]} !important`}
	}

	// disabled style
	background-color: ${props => !!(props.variant === 'contained' && props.disabled) &&
    'rgb(235, 234, 236) !important'};
	border-color: ${props => !!(props.variant === 'bordered' && props.disabled) &&
    'rgb(235, 234, 236) !important'};
	cursor: ${props => props.disabled && 'not-allowed'};

	label {
		color: ${props => !!(props.variant === 'bordered' && props.disabled) &&
    'rgb(235, 234, 236) !important'};
	}
	.file_button_design {
		display: flex;
		align-items: center;
		max-width: 150px;

		.ellipsis {
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
		.grow-1 {
		}

		${({ theme }) => theme.breakpoints.down('DESKTOP')} {
			max-width: 100%;
		}
	}
`;

const ExBottomSheet = ({ show, setShow, title, caption, children, primaryLabel, secondaryLabel, onPrimaryClick, onSecondaryClick, overflow, closeIconClassName, primaryClassName, secondaryClassName, }) => {
    const handleOnClose = () => {
        setShow(false);
    };
    const handleOnPrimaryCtaClick = (e) => {
        e.stopPropagation();
        onPrimaryClick && onPrimaryClick();
    };
    const handleOnSecondaryCtaClick = (e) => {
        e.stopPropagation();
        onSecondaryClick && onSecondaryClick();
    };
    return (jsxs(Fragment, { children: [jsxs(ExBottomSheetLayout, Object.assign({ show: show }, { children: [jsxs(Content, Object.assign({ overflow: overflow }, { children: [title && (jsxs(TitleBox, { children: [jsxs(Title$2, { children: [jsx("span", Object.assign({ className: "title" }, { children: title })), jsx("span", Object.assign({ className: "caption" }, { children: caption }))] }), jsx("div", Object.assign({ className: `xicon-wrap ${closeIconClassName}`, role: "button", tabIndex: 0, css: css `
									display: flex;
								`, onKeyDown: handleOnClose, onClick: handleOnClose }, { children: jsx("span", Object.assign({ className: "xicon-" }, { children: "close_style_border" })) }))] })), jsx("section", Object.assign({ css: css `
							flex: 1;
							overflow-y: auto;
						` }, { children: children }))] })), jsxs("section", Object.assign({ css: theme => css `
						display: flex;
						gap: 16px;
						justify-content: ${isNil(secondaryLabel)
                            ? 'center'
                            : 'space-between'};

						> div {
							${theme.breakpoints.down('TABLET')} {
								flex: 1;
							}
						}
					` }, { children: [!isNil(secondaryLabel) && (jsx(ExButton, { variant: 'contents', size: 'large', label: secondaryLabel, className: secondaryClassName, onClick: handleOnSecondaryCtaClick })), jsx(ExButton, { variant: 'contained', size: 'large', className: primaryClassName, label: primaryLabel, width: isNil(secondaryLabel) ? 185 : 106, onClick: handleOnPrimaryCtaClick })] }))] })), show && (jsx("div", { "aria-hidden": true, onClick: handleOnClose, css: theme => css `
							position: fixed;
							width: 100vw;
							height: 100vh;
							top: 0;
							left: 0;
							z-index: 9998;
							background: ${theme.color.black};
							opacity: ${show ? '0.5' : '0'};
							transition: opacity 0.3 ease-in-out;
						` }))] }));
};
const ExBottomSheetLayout = styled.div `
	position: fixed;
	box-sizing: border-box;
	bottom: ${({ show }) => (show ? '0' : '-100%')};
	left: 0;
	right: 0;
	max-height: 85%;
	z-index: 9999;
	display: flex;
	flex-direction: column;

	gap: 24px;
	padding: 32px;
	border-radius: 24px 24px 0 0;
	background-color: ${({ theme }) => theme.color.white};
	${({ theme }) => theme.elevation.elevation4};
	transition: bottom 0.3s ease-in-out;

	${({ theme }) => theme.breakpoints.down('TABLET')} {
		padding: 24px 16px 40px 16px;
		${({ theme }) => theme.elevation.elevation3};
	}
`;
const Content = styled.div `
	display: flex;
	flex-direction: column;
	gap: 24px;
	overflow: ${({ overflow }) => (overflow ? overflow : 'unset')};

	${({ theme }) => theme.breakpoints.down('TABLET')} {
		gap: 16px;
	}
`;
const TitleBox = styled.div `
	display: flex;
	gap: 24px;
	border-bottom: 1px solid ${({ theme }) => theme.color.mono[10]};

	.xicon-wrap {
		height: fit-content;
	}

	.xicon- {
		font-size: 24px;
		color: ${({ theme }) => theme.color.mono[20]};
	}
`;
const Title$2 = styled.div `
	flex: 1;
	display: flex;
	flex-direction: column;
	padding: 0 0 16px 8px;
	gap: 8px;

	> .title {
		color: ${({ theme }) => theme.color.black};
		${({ theme }) => theme.font.Title2_h};

		${({ theme }) => theme.breakpoints.down('TABLET')} {
			${({ theme }) => theme.font.Body_h};
		}
	}

	> .caption {
		color: ${({ theme }) => theme.color.mono['60']};
		${({ theme }) => theme.font.Caption};

		${({ theme }) => theme.breakpoints.down('TABLET')} {
			${({ theme }) => theme.font.Caption};
		}
	}
`;

/**
 * 체크박스
 *
 * @example ../doc/ExCheck.md
 */
const ExCheck = ({ name, label, size = 'default', disabled = false, caption, labelFont, noNeedPadding, rules, callbackOnChange, className, }) => {
    const { field: { ref, onChange, value }, fieldState: { error }, } = useController({ name, rules });
    const handleOnClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (disabled)
            return;
        if (callbackOnChange)
            callbackOnChange(!value);
        onChange(!value);
    };
    return (jsxs(Check, Object.assign({ size: size, className: className, checked: value, disabled: disabled, onClick: handleOnClick, hasLabel: !!label, noNeedPadding: noNeedPadding }, { children: [jsx(InputCheck, Object.assign({ checked: value, disabled: disabled }, { children: jsx("span", Object.assign({ className: "xicon-" }, { children: value ? 'checkbox_state_checked' : 'checkbox_state_normal' })) })), jsx("input", { type: "checkbox", readOnly: true, ref: ref }), jsxs("div", { children: [jsx(Label$4, Object.assign({ size: size, checked: value, disabled: disabled, labelFont: labelFont }, { children: label })), !isNil(caption) && (jsx(Caption$1, Object.assign({ checked: value }, { children: caption })))] })] })));
};
/**
 * 체크 박스
 * - Hook Form 으로 컨트롤되지 않는 컴포넌트
 *
 * @category Component
 */
const ExCheckUncontrolled = forwardRef(({ label, size = 'default', disabled = false, caption, onChange, noNeedPadding, className, }, ref) => {
    const [checked, setChecked] = useState({ checked: false, indeterminate: true });
    useImperativeHandle(ref, () => ({
        setChecked: (checked, indeterminate) => {
            setChecked({ checked, indeterminate });
        },
    }));
    /**
     * 체크 상태 변경 이벤트 콜백 함수
     * @param event
     */
    const handleOnChange = (event) => {
        setChecked(prevState => {
            return {
                checked: !prevState.checked,
                indeterminate: false,
            };
        });
        if (!isNil(onChange)) {
            onChange(!checked.checked);
        }
    };
    return (jsxs(Check, Object.assign({ size: size, checked: checked.checked, disabled: disabled, hasLabel: !!label, noNeedPadding: noNeedPadding, className: className }, { children: [jsx(InputCheck, Object.assign({ checked: checked.checked, indeterminate: checked.indeterminate, disabled: disabled }, { children: jsx("span", Object.assign({ className: "xicon-" }, { children: checked.checked
                        ? checked.indeterminate
                            ? 'checkbox_state_half'
                            : 'checkbox_state_checked'
                        : 'checkbox_state_normal' })) })), jsx("input", { type: "checkbox", readOnly: true, onChange: handleOnChange }), jsxs("div", { children: [label && (jsx(Label$4, Object.assign({ size: size, checked: checked.checked, disabled: disabled }, { children: label }))), !isNil(caption) && (jsx(Caption$1, Object.assign({ checked: checked.checked }, { children: caption })))] })] })));
});
const Check = styled.label `
	display: flex;
	width: fit-content;
	gap: 12px;
	align-items: start;
	padding: ${({ noNeedPadding }) => noNeedPadding ? 0 : '8px 16px 8px 12px'};
	border-radius: 8px;
	background-color: ${props => props.checked && !props.disabled
    ? props.theme.color.primary[10]
    : props.theme.color.white};
	cursor: ${props => (props.disabled ? 'default' : 'pointer')};
	input {
		position: absolute;
		opacity: 0;
		width: 0;
		height: 0;
	}
	> div:last-of-type {
		display: ${({ hasLabel }) => (hasLabel ? 'flex' : 'none')};
		flex-direction: column;
		gap: ${props => (props.size === 'small' ? '8px' : '12px')};
		padding: ${props => (props.size === 'small' ? '2px 0px' : '0px')};
		cursor: inherit;
	}
`;
const InputCheck = styled.div `
	cursor: inherit;
	span {
		color: ${props => props.disabled
    ? props.theme.color.mono[10]
    : props.checked
        ? props.indeterminate
            ? props.theme.color.primary[30]
            : props.theme.color.primary[60]
        : props.theme.color.mono[20]};
		font-size: 24px;
		cursor: inherit;
	}
`;
const Label$4 = styled.span `
	cursor: inherit;
	${props => props.size === 'small'
    ? props.theme.font.Subtitle2
    : props.labelFont
        ? props.labelFont
        : props.theme.font.Body1};
	color: ${props => props.disabled
    ? props.theme.color.mono[20]
    : props.checked
        ? props.theme.color.primary[80]
        : props.theme.color.black};
`;
const Caption$1 = styled.span `
	color: ${props => props.checked
    ? props.theme.color.primary[50]
    : props.theme.color.mono[40]};
	${props => props.theme.font.Caption};
`;

const ExHashTag = ({ state, setState, variant, backgroundColor, fontColor, borderColor, activeBackgroundColor, activeFontColor, activeBorderColor, }) => {
    const handleOnClickToggle = (variant, index, toggle) => {
        if (variant === 'toggle') {
            const arr = [...state];
            arr[index].toggle = !toggle;
            setState(arr);
        }
    };
    const handleOnClickRemoveHashTag = (index) => {
        const arr = [...state];
        arr.splice(index, 1);
        setState(arr);
    };
    return (jsx(ExHashTagBox, Object.assign({ variant: variant, borderColor: borderColor, fontColor: fontColor, backgroundColor: backgroundColor }, { children: state.map((hashTag, index) => variant === 'toggle' ? (jsxs(ToggleBox, Object.assign({ activeBackgroundColor: activeBackgroundColor, activeBorderColor: activeBorderColor, activeFontColor: activeFontColor, className: hashTag.toggle && variant === 'toggle'
                ? 'checked'
                : undefined, onClick: () => handleOnClickToggle(variant, index, hashTag.toggle) }, { children: [hashTag.toggle ? (jsx("span", Object.assign({ className: "xicon-" }, { children: "checkbox_state_just_check_icon" }))) : (jsx("span", Object.assign({ className: "xicon-" }, { children: "add" }))), jsx("span", { children: hashTag.label })] }), index)) : variant === 'usermade' ? (jsxs("div", Object.assign({ css: css `
							padding: 8px 12px 8px 16px;
						` }, { children: [jsx("span", { children: hashTag.label }), jsx("span", Object.assign({ className: "xicon-", "aria-hidden": "true", onClick: () => handleOnClickRemoveHashTag(index) }, { children: "close_style_border" }))] }))) : (variant === 'static' && (jsx("div", Object.assign({ css: css `
								padding: 8px 16px 8px 16px;
							` }, { children: hashTag.label }))))) })));
};
const ExHashTagBox = styled.div `
	display: flex;
	gap: 15px;

	> div {
		display: flex;
		gap: 8px;
		border-radius: 100px;
		height: 36px;
		align-items: center;
		${({ theme }) => theme.font.Body2};
		background-color: ${({ backgroundColor, theme, variant }) => backgroundColor
    ? backgroundColor
    : variant === 'toggle'
        ? theme.color.white
        : theme.color.primary['10']};
		border: ${({ borderColor, theme, variant }) => borderColor
    ? borderColor
    : variant === 'toggle'
        ? `1px solid ${theme.color.primary['20']}`
        : `1px solid ${theme.color.primary['10']}`};
		color: ${({ fontColor, theme }) => fontColor ? fontColor : theme.color.primary['70']};

		.xicon- {
			font-size: 20px;
			cursor: ${({ variant }) => variant === 'usermade' ? 'pointer' : 'unset'};
		}
	}
`;
const ToggleBox = styled.div `
	cursor: pointer;
	padding: 8px 16px 8px 12px;

	&.checked {
		background-color: ${({ activeBackgroundColor, theme }) => activeBackgroundColor
    ? activeBackgroundColor
    : theme.color.primary['10']};
		border: ${({ activeBorderColor, theme }) => activeBorderColor
    ? activeBorderColor
    : `1px solid ${theme.color.primary['10']}`};
		color: ${({ activeFontColor, theme }) => activeFontColor ? activeFontColor : theme.color.primary['70']};
	}
`;

/**휴대폰번호 입력 시 '-' 붙이는 함수 */
const phoneNumWithInputHyphens = (value) => {
    if (!value) {
        return '';
    }
    value = value.replace(/[^0-9]/g, '');
    const result = [];
    let restNumber = '';
    result.push(value.substring(0, 3));
    restNumber = value.substring(3);
    if (restNumber.length === 7) {
        //010-xxx-xxxx인 경우
        result.push(restNumber.substring(0, 3));
        result.push(restNumber.substring(3));
    }
    else {
        //010-xxxx-xxxx인 경우
        result.push(restNumber.substring(0, 4));
        result.push(restNumber.substring(4));
    }
    return result.filter(val => val).join('-');
};
/**사업자등록번호 입력시 '-' 붙이는 함수 */
const businessNumberWithInputHyphens = (value) => {
    if (!value) {
        return '';
    }
    value = value.replace(/[^0-9]/g, '');
    const result = [];
    let restNumber = '';
    result.push(value.substring(0, 3));
    restNumber = value.substring(3);
    if (restNumber.length === 5) {
        // 5자리만 남았을 때는 xx-yyy
        result.push(restNumber.substring(0, 2));
        result.push(restNumber.substring(2));
    }
    else {
        result.push(restNumber.substring(0, 2));
        result.push(restNumber.substring(2));
    }
    return result.filter(val => val).join('-');
};

// export type TIMER_STATUS = 'READY' | 'COUNTING' | 'STOP' | 'TERMINATION';
const TIMER_STATUS = {
    READY: 'READY',
    COUNTING: 'COUNTING',
    STOP: 'STOP',
    TERMINATION: 'TERMINATION',
};
const callbackTimerTermination = (timerStatus, inputName, setError, timerTerminationMessage) => {
    if (timerStatus === TIMER_STATUS.TERMINATION)
        setError(inputName, {
            type: 'timer',
            message: timerTerminationMessage
                ? timerTerminationMessage
                : '시간이 초과되었습니다',
        });
};

const ExInput = forwardRef(({ type, name, password, label, rules, placeholder, readonly = false, disabled = false, leftIconName, rightIconName, leftIconColor, rightIconColor, leftIconCursor, rightIconCursor, unitAdornment, counterAdornment, timerAdornment, timerFinishedMessage, timerStatusCallBack, caption, width, height, inputValue, numberComma = false, numberOnly = false, floatingPoint = undefined, inputSize, callbackOnBlur, callbackOnClick, callbackOnChange, callbackOnInputKeyDown, callbackOnLeftIconClick, callbackOnRightIconClick, textAlign, isDateField, phoneNumAutoHyphen, businessAutoHyphen, hasRulesButNoErrorCaption = false, }, forwardRef) => {
    var _a, _b, _c, _d, _e, _f;
    const { field: { onBlur, onChange, value }, fieldState: { error }, } = useController({ name, rules });
    const { setError } = useFormContext();
    const [timer, setTimer] = useState(timerAdornment);
    const [timerStatus, setTimerStatus] = useState(TIMER_STATUS.READY);
    const [isFocus, setIsFocus] = useState(false);
    const textAreaRef = useRef(null);
    const isDisabled = readonly || disabled;
    useImperativeHandle(forwardRef, () => timerAdornment
        ? {
            timerStart() {
                setTimer(timerAdornment);
                setTimerStatus(TIMER_STATUS.COUNTING);
            },
            timerStop() {
                setTimerStatus(TIMER_STATUS.STOP);
            },
            timerInit() {
                setTimerStatus(TIMER_STATUS.READY);
            },
        }
        : {
            timerStart() {
                /**timerStart */
            },
            timerStop() {
                /**timerStop */
            },
            timerInit() {
                /**timerInit */
            },
        });
    /** input창 클릭 시 callback 함수 */
    const handleOnClick = () => {
        if (callbackOnClick)
            callbackOnClick();
    };
    /**왼쪽 아이콘 클릭 시 callback 함수 */
    const handleOnLeftIconClick = () => {
        if (callbackOnLeftIconClick)
            callbackOnLeftIconClick();
    };
    /**오른쪽 아이콘 클릭 시 callback 함수 */
    const handleOnRightIconClick = () => {
        if (callbackOnRightIconClick)
            callbackOnRightIconClick();
    };
    /**
     * Input onFocus 이벤트 callback함수
     * @param e
     */
    const handleOnFocus = (e) => {
        if (!error)
            setIsFocus(true);
    };
    /**
     * input onBlur 이벤트 callback함수
     * @param e
     *
     */
    const handleOnBlur = (e) => {
        if (isDateField)
            return;
        onBlur();
        if (callbackOnBlur)
            callbackOnBlur();
        setIsFocus(false);
    };
    /**
     *  input onChange 이벤트 callback 함수
     * @param e
     *
     */
    const handleOnChange = (e) => {
        const { value } = e.target;
        if (callbackOnChange)
            callbackOnChange();
        onChange(value);
        /**금액에서 3자리마다 ',' 찍기 */
        if (numberComma) {
            /**입력받은 값에서 숫자외의 문자들 제거 */
            const onlyNumberValue = value.replace(/[^0-9]/g, '');
            // .replace(/[^0+]/g, '');
            // if (onlyNumberValue.length > rules?.maxLength?.value) return;
            onChange(onlyNumberValue.split(/(?=(?:\d{3})+$)/).join(','));
            return;
        }
        /**휴대폰번호 입력시 '-' 붙이기*/
        if (phoneNumAutoHyphen) {
            const phoneNumResult = phoneNumWithInputHyphens(value);
            onChange(phoneNumResult);
        }
        /**사업자등록번호 입력시 '-'붙이기 */
        if (businessAutoHyphen) {
            const businessNumResult = businessNumberWithInputHyphens(value);
            onChange(businessNumResult);
        }
        /**부동소숫점 처리 */
        if (floatingPoint) {
            const splited = String(value).split('.');
            if (splited.length > 1)
                if (splited[1].length > floatingPoint)
                    return;
        }
        if (!error)
            setIsFocus(true);
    };
    const handleOnKeyDown = (e) => {
        if (callbackOnInputKeyDown)
            callbackOnInputKeyDown(e);
    };
    /**타이머를 위한 useEffect */
    useEffect(() => {
        if (timer === undefined)
            return;
        if (timer <= 0) {
            setTimerStatus(TIMER_STATUS.TERMINATION);
            if (timerAdornment)
                callbackTimerTermination(TIMER_STATUS.TERMINATION, name, setError, timerFinishedMessage);
            if (timerStatusCallBack)
                timerStatusCallBack(TIMER_STATUS.TERMINATION);
            return;
        }
        if (timerStatus !== TIMER_STATUS.COUNTING)
            return;
        const clock = setInterval(() => {
            setTimer(timer - 1);
        }, 1000);
        return () => {
            clearInterval(clock);
        };
    }, [timer, timerStatus]);
    /**inputSize가 desktop인지 */
    const isDesktop = inputSize === 'desktop';
    /**inputSize가 mobile인지 */
    const isMobile = inputSize === 'mobile';
    return (jsx(Fragment, { children: type === 'input' ? (jsxs(Layout, Object.assign({ width: width }, { children: [jsxs(InputLayout, Object.assign({ width: width, isDesktop: isDesktop, isMobile: isMobile, isError: error, isFocus: isFocus, readonly: readonly, isDisabled: isDisabled, isDateField: isDateField }, (isDateField && { onClick: handleOnClick }), { children: [label && (jsx(Label$3, Object.assign({ className: isFocus || (value === null || value === void 0 ? void 0 : value.length) > 0
                                ? 'visible'
                                : 'none_visible', isError: error, readonly: readonly, isDisabled: isDisabled, isFocus: isFocus, valueLength: value === null || value === void 0 ? void 0 : value.length }, { children: label }))), jsxs(Container, { children: [leftIconName && (jsx(Icon$1, Object.assign({ isDesktop: isDesktop, isMobile: isMobile, isError: error, iconPos: "left", leftIconColor: leftIconColor, iconCursor: leftIconCursor, onClick: handleOnLeftIconClick, isDisabled: isDisabled, readonly: readonly }, { children: jsx("span", Object.assign({ className: "xicon-" }, { children: leftIconName })) }))), jsx(Input, Object.assign({ type: numberOnly
                                        ? 'number'
                                        : password
                                            ? 'password'
                                            : 'text', name: name, placeholder: placeholder, isDesktop: isDesktop, isError: error, value: value || '', readonly: readonly, onBlur: handleOnBlur, onChange: handleOnChange, onFocus: handleOnFocus, onClick: handleOnClick, onKeyDown: e => handleOnKeyDown(e) }, (inputValue && {
                                    value: inputValue,
                                }), { disabled: isDisabled || isDateField, isDateField: isDateField, textAlign: textAlign, maxLength: (_a = rules === null || rules === void 0 ? void 0 : rules.maxLength) === null || _a === void 0 ? void 0 : _a.value, autoComplete: "off" })), timerAdornment &&
                                    timerStatus !== TIMER_STATUS.READY && (jsx(Timer, Object.assign({ isDesktop: isDesktop, isMobile: isMobile, isError: error, isDisabled: isDisabled, readonly: readonly }, { children: timer !== undefined && (jsxs("span", { children: [Math.floor(timer / 60), ":", String(timer % 60).padStart(2, '0')] })) }))), unitAdornment && (jsx(Unit, Object.assign({ isDesktop: isDesktop, isMobile: isMobile }, { children: unitAdornment }))), rightIconName && (jsx(Icon$1, Object.assign({ isDesktop: isDesktop, isMobile: isMobile, isError: error, iconPos: "right", rightIconColor: rightIconColor, iconCursor: rightIconCursor, onClick: handleOnRightIconClick, isDisabled: isDisabled, readonly: readonly }, { children: jsx("span", Object.assign({ className: "xicon-" }, { children: rightIconName })) })))] })] })), !hasRulesButNoErrorCaption && (error || caption) && (jsxs(Caption, Object.assign({ error: error }, { children: [caption && !error && jsx("span", { children: caption }), error && jsx("span", { children: error === null || error === void 0 ? void 0 : error.message })] })))] }))) : (
        //type이 textArea일 때
        jsxs(Layout, { children: [jsxs(TextAreaLayout, Object.assign({ height: height }, { children: [counterAdornment && (jsx(CounterAdornment, { children: jsx("span", { children: `입력 글자 수 : ${(_d = (_c = (_b = textAreaRef === null || textAreaRef === void 0 ? void 0 : textAreaRef.current) === null || _b === void 0 ? void 0 : _b.value) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0}/${(_e = rules === null || rules === void 0 ? void 0 : rules.maxLength) === null || _e === void 0 ? void 0 : _e.value}` }) })), jsx(TextArea, { onBlur: handleOnBlur, onChange: handleOnChange, onFocus: handleOnFocus, onClick: handleOnClick, inputSize: inputSize, isFocus: isFocus, isDesktop: isDesktop, ref: textAreaRef, value: readonly ? inputValue : value || '', width: width, height: height, placeholder: placeholder, disabled: disabled || readonly, readonly: readonly, isError: error, isDisabled: isDisabled, maxLength: (_f = rules === null || rules === void 0 ? void 0 : rules.maxLength) === null || _f === void 0 ? void 0 : _f.value })] })), !hasRulesButNoErrorCaption && (error || caption) && (jsxs(Caption, Object.assign({ error: error }, { children: [caption && !error && jsx("span", { children: caption }), error && jsx("span", { children: error === null || error === void 0 ? void 0 : error.message })] })))] })) }));
});
const fadeIn = keyframes `
	from{
		opactiy : 0;
	}
	to{
		opacity : 1;
	}
`;
keyframes `
	from{
		opactiy : 1;
	}
	to{
		opacity : 0;
	}
`;
const Layout = styled.div `
	display: flex;
	flex-direction: column;
	width: ${({ width }) => (width ? width : '100%')};
	gap: 4px;
`;
const InputLayout = styled.div `
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: ${({ isDesktop }) => (isDesktop ? '4px' : '2px')};
	height: ${({ isDesktop, isMobile }) => isDesktop ? '60px' : isMobile ? '50px' : '32px'};
	width: ${({ width }) => (width ? width : '100%')};
	border: ${({ theme, isFocus, isError, isDisabled, readonly }) => isDisabled && !readonly
    ? `1px solid ${theme.color.mono[10]}`
    : isError
        ? `1px solid ${theme.color.red[60]}`
        : isFocus
            ? `1px solid ${theme.color.primary[60]}`
            : `1px solid ${theme.color.primary[20]}`};
	padding: ${({ isDesktop, isMobile }) => isDesktop ? '8px 16px' : isMobile ? '6px 12px' : '6px 16px'};
	background: ${({ theme, isError, readonly, isDisabled }) => isError
    ? theme.color.red[10]
    : isDisabled || readonly
        ? theme.color.primary[5]
        : theme.color.white};

	.visible {
		display: block;
		animation: ${fadeIn} forwards;
	}
	.none_visible {
		display: none;
		opacity: 0;
	}

	cursor: ${({ isDateField }) => (isDateField ? 'pointer' : 'unset')};
	input:-webkit-autofill {
		-webkit-box-shadow: ${({ theme, readonly, isError }) => `0 0 0 1000px ${isError
    ? theme.color.red[10]
    : readonly
        ? theme.color.primary[5]
        : '#fff'}  inset`};
		-webkit-text-fill-color: ${({ theme, readonly, isError }) => isError
    ? theme.color.red[60]
    : readonly
        ? theme.color.mono[70]
        : 'inital'};
	}
`;
const Label$3 = styled.p `
	display: ${({ isFocus, valueLength }) => isFocus || valueLength > 0 ? 'block' : 'none'};
	opacity: 0;
	color: ${({ theme, isError, readonly, isDisabled }) => isDisabled && !readonly
    ? theme.color.mono[30]
    : isError
        ? theme.color.red[60]
        : readonly
            ? theme.color.mono[70]
            : theme.color.primary[90]};
	${({ theme }) => theme.font.Overline}
	transition: 0.5s all ease;
`;
const Container = styled.div `
	display: flex;
	gap: 4px;
	align-items: center;
`;
const Input = styled.input `
	padding: 0;
	margin: 0;
	height: ${({ isDesktop }) => (isDesktop ? '24px' : '20px')};
	line-height: ${({ isDesktop }) => (isDesktop ? '24px' : '20px')};
	width: 100%;
	-webkit-text-fill-color: ${({ theme, isError, readonly, disabled, isDateField, }) => disabled && !isDateField && !readonly
    ? theme.color.mono[30]
    : isError
        ? theme.color.red[60]
        : readonly
            ? `rgba(92, 91, 94, 1)` //mono[70]과 동일
            : 'unset'};
	-webkit-opacity: 1;
	/* flex-grow: 1; */
	text-align: ${({ textAlign }) => (textAlign ? textAlign : 'initial')};
	border: none;
	outline: none;
	background: transparent;
	${({ theme, isDesktop }) => isDesktop ? theme.font.Body1 : theme.font.Subtitle2}
	cursor: ${({ isDateField }) => (isDateField ? 'pointer' : 'initial')};

	:focus::placeholder {
		-webkit-text-fill-color: transparent;
	}
	::placeholder {
		-webkit-text-fill-color: ${({ theme, isError, disabled, isDateField, readonly, }) => readonly && isError
    ? theme.color.red[20]
    : disabled && !isDateField
        ? theme.color.mono[20]
        : isError
            ? theme.color.red[20]
            : theme.color.mono[30]};
		${({ theme, isDesktop }) => isDesktop ? theme.font.Body1 : theme.font.Subtitle2};
	}
	/* Chrome, Safari, Edge, Opera에서 type이 number일때 증가 감소 버튼 제거 */
	::-webkit-outer-spin-button,
	::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	/* Firefox type이 number일때 증가 감소 버튼 제거 */
	-moz-appearance: textfield;
`;
const Timer = styled.div `
	display: flex;
	justify-content: center;
	height: ${({ isDesktop, isMobile }) => isDesktop ? '24px' : isMobile ? '18px' : '20px'};
	align-items: center;
	padding: ${({ isDesktop, isMobile }) => isDesktop ? '3px 4px' : isMobile ? '0px 2px' : '0px 4px'};
	background: ${({ theme, isError, isDisabled }) => isDisabled
    ? theme.color.mono[10]
    : isError
        ? theme.color.red[20]
        : theme.color.mono[10]};

	> span {
		${({ theme }) => theme.font.Caption}
		color: ${({ theme, isError, isDisabled, readonly }) => isDisabled && !readonly
    ? theme.color.mono[30]
    : isError
        ? theme.color.white
        : theme.color.mono[60]};
		text-align: center;
		box-sizing: border-box;
	}
`;
const Icon$1 = styled.div `
	width: ${({ isDesktop, isMobile }) => isDesktop ? '24px' : isMobile ? '18px' : '20px'};
	height: ${({ isDesktop, isMobile }) => isDesktop ? '24px' : isMobile ? '18px' : '20px'};
	cursor: ${({ iconCursor }) => (iconCursor ? 'pointer' : 'unset')};
	> span {
		font-size: ${({ isDesktop, isMobile }) => isDesktop ? '24px' : isMobile ? '18px' : '20px'};
		color: ${({ theme, iconPos, leftIconColor, rightIconColor, isError, isDisabled, readonly, }) => isDisabled && !readonly && iconPos === 'left'
    ? theme.color.mono[10]
    : isDisabled && !readonly && iconPos === 'right'
        ? theme.color.mono[20]
        : iconPos === 'left' && leftIconColor
            ? leftIconColor
            : iconPos === 'right' && rightIconColor
                ? rightIconColor
                : isError && iconPos === 'left'
                    ? theme.color.red[20]
                    : isError && iconPos === 'right'
                        ? theme.color.red[60]
                        : theme.color.primary[20]};
	}
`;
const Unit = styled.div `
	display: flex;
	justify-content: center;
	align-items: center;
	${({ theme }) => theme.font.Caption};
	color: ${({ theme }) => theme.color.mono[60]};
	padding: ${({ isDesktop, isMobile }) => isDesktop ? '3px 4px' : isMobile ? '0 2px' : '0 4px'};
`;
const TextAreaLayout = styled.div `
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: 100%;
	height: ${({ height }) => (height ? height : '100%')};
`;
const TextArea = styled.textarea `
	resize: none;
	overflow: auto;
	padding: ${({ isDesktop }) => (isDesktop ? '16px' : '12px')};
	width: ${({ width }) => (width ? width : '100%')};
	height: ${({ height }) => (height ? height : '100%')};
	border: ${({ theme, isError, isDisabled, isFocus }) => isError
    ? `1px solid ${theme.color.red[60]}`
    : isDisabled
        ? `1px solid ${theme.color.mono[10]}`
        : isFocus
            ? `1px solid ${theme.color.primary[60]}`
            : `1px solid ${theme.color.primary[20]}`};

	background: ${({ theme, readonly, isError, isDisabled }) => isError
    ? theme.color.red[10]
    : isDisabled || readonly
        ? theme.color.primary[5]
        : theme.color.white};
	flex-grow: 1;
	color: ${({ theme, readonly, isError }) => readonly
    ? theme.color.mono[70]
    : isError
        ? theme.color.red[60]
        : theme.color.black};
	outline: none;
	${({ theme, isDesktop }) => isDesktop ? theme.font.Body1 : theme.font.Subtitle2}

	:focus::placeholder {
		color: transparent;
	}

	::placeholder {
		${({ theme, inputSize }) => inputSize === 'desktop' ? theme.font.Body1 : theme.font.Subtitle2}
		color:${({ theme, isError, isDisabled }) => isError
    ? theme.color.red[20]
    : isDisabled
        ? theme.color.mono[20]
        : theme.color.mono[30]}
	}
`;
const CounterAdornment = styled.div `
	padding: 0 4px;
	text-align: end;

	> span {
		${({ theme }) => theme.font.Caption}
		color:${({ theme }) => theme.color.mono[60]}
	}
`;
const Caption = styled.div `
	padding: 0 16px;

	> span {
		${({ theme }) => theme.font.Caption}
		color:${({ theme, error }) => error ? theme.color.red[60] : theme.color.mono[60]}
	}
`;

const ExLabel = ({ variant, fontColor, label, backgroundColor, }) => {
    const theme = useTheme();
    const getFontColor = () => {
        if (variant === 'bordered')
            return fontColor;
        //variant가 contained일 때
        switch (backgroundColor) {
            case '#7A37D8': //primary60
            case '#CDCCCF': //mono60
            case '#4DBFB8': //secondary60
            case '#FF3F56': //red[60]
                return theme.color.white;
            case '#F7F7F7':
                return theme.color.mono[40];
            default:
                return fontColor;
        }
    };
    const getBackgroundColor = () => {
        if (variant === 'contained' && backgroundColor) {
            return backgroundColor;
        }
        if (variant === 'bordered' && fontColor) {
            if (fontColor) {
                switch (fontColor) {
                    case '#7A37D8': //primary60
                        return theme.color.primary[10];
                    case '#727174': //mono60
                        return theme.color.mono[5];
                    case '#4DBFB8': //secondary60
                        return theme.color.secondary[10];
                    case '#FF3F56': //red[60]
                        return theme.color.red[10];
                    case '#B4B4B6': //mono[30]
                        return theme.color.mono[5];
                }
            }
        }
    };
    return (jsx(LabelLayout, Object.assign({ variant: variant, fontColor: getFontColor(), backgroundColor: getBackgroundColor() }, { children: jsx(Label$2, { children: label }) })));
};
const LabelLayout = styled.div `
	display: inline-flex;
	justify-content: center;
	align-items: center;
	padding: 2px 12px;
	border: ${({ variant, fontColor, backgroundColor }) => variant === 'contained'
    ? `2px solid ${backgroundColor}`
    : `2px solid ${fontColor}`};
	border-radius: 2px;
	background: ${({ backgroundColor }) => backgroundColor};
	color: ${({ fontColor }) => fontColor};
	height: 22px;
`;
const Label$2 = styled.span `
	${({ theme }) => theme.font['Caption_h']}
`;

const ExLabelStatus = ({ statusColor, borderStyle, fontColor, }) => {
    return (jsxs(ExLabelStatusLayout, Object.assign({ borderStyle: borderStyle }, { children: [jsx(DisplayStatus, { statusColor: statusColor }), jsx(Label$1, Object.assign({ fontColor: fontColor }, { children: "value" }))] })));
};
const ExLabelStatusLayout = styled.div `
	display: inline-flex;
	justify-content: center;
	align-items: center;
	padding: 1px 8px;
	height: 20px;
	gap: 10px;
	border: ${({ theme, borderStyle }) => borderStyle ? borderStyle : `1px solid ${theme.color.mono[5]}`};
	border-radius: 100px;
`;
const DisplayStatus = styled.div `
	width: 8px;
	height: 8px;
	background: ${({ statusColor }) => statusColor};
	border-radius: 50%;
`;
const Label$1 = styled.span `
	${({ theme }) => theme.font['Caption_h']}
	color:${({ theme, fontColor }) => fontColor ? fontColor : theme.color.mono[40]}
`;

const ExLabelTag = ({ variant = 'default', label, fontColor, borderStyle, backgroundColor, }) => {
    return (jsxs(ExlabelTagLayout, Object.assign({ variant: variant, borderStyle: borderStyle, fontColor: fontColor, backgroundColor: backgroundColor }, { children: ["# ", label] })));
};
const ExlabelTagLayout = styled.span `
	${({ theme }) => theme.font.Caption}
	border: ${({ theme, borderStyle, variant }) => borderStyle
    ? borderStyle
    : variant === 'default'
        ? `1px solid ${theme.color.mono[10]}`
        : `1px solid ${theme.color.primary[20]}`};
	border-radius: 100px;
	padding: 4px 12px;
	height: 26px;
	color: ${({ theme, variant, fontColor }) => fontColor
    ? fontColor
    : variant === 'default'
        ? theme.color.mono[60]
        : theme.color.primary[5]};

	background: ${({ backgroundColor }) => backgroundColor ? backgroundColor : 'inherit'};
`;

const ExPagination = ({ postsLimit, currPage, totalPostsCount, setCurrPage, settedPaginationLength, gap, mdArrow, isdependancy, onPageChange, }) => {
    const theme = useTheme();
    /**startNum을 변경만을 위한 state*/
    const [pageNumForStartNum, setPageNumForStartNum] = useState(currPage);
    /**pagintaion에서 가장 첫번째 숫자 (ex. settedPaginationLength가 10이면 1,11,21...) */
    const startNum = pageNumForStartNum - (pageNumForStartNum % settedPaginationLength) + 1;
    /**pagination 길이 */
    const totalPageLength = Math.ceil(totalPostsCount / postsLimit);
    /**설정한 pagination길이보다  더 많은지*/
    const isMoreThenPaginationLength = totalPageLength > settedPaginationLength;
    /**page번호 클릭했을 때 */
    const handleOnPageNum = (currIndex, e) => {
        if (onPageChange) {
            onPageChange(startNum + 1 + currIndex);
        }
        setCurrPage(startNum + 1 + currIndex);
        /**게시글 detail page 진입 */
        sessionStorage.setItem('currPage', String(startNum + 1 + currIndex));
    };
    /**다음 settedPaginationLength개 보여주는 화살표 클릭 */
    const handleOnNextClick = () => {
        if (onPageChange) {
            onPageChange(startNum + settedPaginationLength);
        }
        setCurrPage(startNum + settedPaginationLength);
        setPageNumForStartNum(startNum + settedPaginationLength);
    };
    /**이전 settedPaginationLength개 보여주는 화살표 클릭 */
    const handleOnPrevClick = () => {
        if (onPageChange) {
            onPageChange(startNum + settedPaginationLength);
        }
        setCurrPage(startNum - settedPaginationLength);
        setPageNumForStartNum(startNum - settedPaginationLength);
    };
    /**페이지 1로 가는 버튼 */
    const moveToStartPage = () => {
        if (onPageChange) {
            onPageChange(1);
        }
        setCurrPage(1);
        setPageNumForStartNum(1);
    };
    /**마지막 페이지로 가는 버튼 */
    const moveToFinalPage = () => {
        if (totalPageLength % 10 == 0) {
            setPageNumForStartNum(Math.floor(totalPageLength / 10 - 1) * 10 + 1);
        }
        else {
            setPageNumForStartNum(Math.floor(totalPageLength / 10) * 10 + 1);
        }
        if (onPageChange) {
            onPageChange(totalPageLength);
        }
        setCurrPage(totalPageLength);
    };
    useEffect(() => {
        if (isdependancy) {
            setCurrPage(1);
            setPageNumForStartNum(1);
        }
    }, [isdependancy]);
    return (jsx(Wrapper$2, { children: jsxs("div", Object.assign({ css: css `
					display: ${totalPostsCount > 10 ? 'flex' : 'none'};
					justify-content: center;
					align-items: center;
					gap: 16px;
					margin-right: ${totalPostsCount <= 100 ? '48px' : '0px'};
				` }, { children: [isMoreThenPaginationLength && startNum !== 1 && (jsxs("div", Object.assign({ css: css `
							display: flex;
						` }, { children: [jsx(ExButton, { variant: "contents", size: "tiny", icon: "chevron_double_left", onClick: moveToStartPage, iconColor: `${theme.color.mono[70]}` }), jsx(ExButton, { variant: "contents", size: "tiny", icon: "chevron_direction_left", onClick: handleOnPrevClick, iconColor: `${theme.color.mono[70]}` })] }))), jsxs("div", Object.assign({ css: css `
						display: flex;
						gap: 24px;
						margin: 0 auto;
					` }, { children: [jsx(PageNum, Object.assign({ onClick: e => {
                                setCurrPage(startNum);
                            }, isCurrPage: currPage === startNum }, { children: startNum })), Array(totalPageLength >= settedPaginationLength
                            ? settedPaginationLength - 1
                            : totalPageLength === 0
                                ? 1
                                : totalPageLength - 1)
                            .fill(0)
                            .map((_, i) => {
                            return (
                            // <Link to={`/pagination?page=${startNum + i + 1}`}>
                            jsx(PageNum, Object.assign({ onClick: e => {
                                    handleOnPageNum(i);
                                }, isCurrPage: currPage === startNum + i + 1 }, { children: startNum + i + 1 <= totalPageLength &&
                                    startNum + i + 1 }), i)
                            // </Link>
                            );
                        })] })), isMoreThenPaginationLength &&
                    startNum + settedPaginationLength <= totalPageLength && (jsxs("div", Object.assign({ css: css `
								display: flex;
							` }, { children: [jsx(ExButton, { variant: "contents", size: "tiny", icon: "chevron_direction_right", onClick: handleOnNextClick, iconColor: `${theme.color.mono[70]}` }), jsx(ExButton, { variant: "contents", size: "tiny", icon: "chevron_double_right", onClick: moveToFinalPage, iconColor: `${theme.color.mono[70]}` })] })))] })) }));
};
const PageNum = styled.span `
	${({ theme, isCurrPage }) => isCurrPage ? theme.font.Subtitle2_h : theme.font.Subtitle2};
	color: ${({ theme, isCurrPage }) => isCurrPage ? theme.color.primary[60] : theme.color.mono[80]};
	cursor: pointer;
`;
// 그냥 span에 onClick event 걸면 eslint errror나서
styled.span `
	cursor: pointer;
	${({ theme }) => theme.color.mono[70]}
`;
const Wrapper$2 = styled.div `
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
`;

const ExRadio = ({ defaultValue, label, caption, disabled, width, name, }) => {
    const [isChecked, setIsChecked] = useState(false);
    const { field: { onChange, value }, } = useController({ name });
    useEffect(() => {
        if (value === defaultValue) {
            setIsChecked(true);
        }
        else {
            setIsChecked(false);
        }
    }, [value]);
    const handleOnClick = (defaultValue) => {
        onChange(defaultValue);
    };
    return (jsxs(ExRadioBox, Object.assign({ isChecked: isChecked, width: width, onClick: () => {
            handleOnClick(defaultValue);
        }, className: disabled ? 'disabled' : undefined }, { children: [jsx("span", Object.assign({ className: "xicon-" }, { children: isChecked ? 'radio_state_checked' : 'radio_state_normal' })), jsxs("div", { children: [jsx("p", { children: label }), jsx("span", { children: caption })] })] })));
};
const ExRadioBox = styled.div `
	width: ${({ width }) => (width ? `${width}px` : '100%')};
	min-height: 70px;
	display: inline-flex;
	align-items: flex-start;
	cursor: pointer;
	gap: 12px;
	padding: 8px 16px 8px 12px;
	border-radius: 8px;
	background-color: ${({ theme, isChecked }) => isChecked ? theme.color.primary['10'] : 'transparent'};

	> span {
		font-size: 24px;
		color: ${({ theme, isChecked }) => isChecked ? theme.color.primary['60'] : theme.color.mono['20']};
	}

	> div {
		display: flex;
		flex-direction: column;
		gap: 12px;

		> p {
			${({ theme }) => theme.font.Body1};
			color: ${({ theme, isChecked }) => isChecked && theme.color.primary['80']};
		}

		> span {
			${({ theme }) => theme.font.Caption};
			color: ${({ theme, isChecked }) => isChecked ? theme.color.primary['50'] : theme.color.mono['40']};
		}
	}

	&.disabled {
		pointer-events: none;

		> span {
			color: ${({ theme }) => theme.color.mono['20']};
		}

		> div {
			> p {
				color: ${({ theme }) => theme.color.mono['20']};
			}

			> span {
				color: ${({ theme }) => theme.color.mono['10']};
			}
		}
	}
`;

const ExRadioGroup = ({ children, gap, direction = 'row' }) => {
    return (jsx(ExRadioGroupLayout, Object.assign({ gap: gap, direction: direction }, { children: children })));
};
const ExRadioGroupLayout = styled.div `
	display: flex;
	flex-direction: ${({ direction }) => direction};
	gap: ${({ gap }) => (gap ? `${gap}px` : '12px')};
	width: 100%;
`;

/**
 * 셀렉트 박스
 *
 * @category Component
 * @param variant - 유형
 * @param size
 * @param name
 * @param options
 * @param label
 * @param width
 * @param placeholder
 * @param disabled
 * @param readonly
 * @param rules
 * @constructor
 */
const ExSelect = ({ variant, size, name, options, label, width, placeholder, disabled = false, readonly = false, rules, caption, onChange, }) => {
    var _a;
    const { field: { ref, onChange: onChangeInner, onBlur, value }, fieldState: { error }, } = useController({ name, rules });
    const [focus, setFocus] = useState(false);
    const hiddenInputRef = useRef(null);
    /**
     * 셀렉트 박스 클릭 이벤트 콜백
     * @param event
     */
    const handleOnClick = (event) => {
        event.preventDefault();
        setFocus(prev => {
            var _a;
            if (!prev) {
                (_a = hiddenInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
            return !prev;
        });
    };
    /**
     * 드롭다운 박스중 아이템 클릭 이벤트 콜백
     * @param itemIndex
     * @param e
     */
    const handleOnItemClick = (itemIndex, e) => {
        onChangeInner(options[itemIndex].value);
        onBlur();
        if (!isNil(onChange) && typeof onChange === 'function') {
            onChange();
        }
    };
    /**
     * 셀렉트박스 + 드롭다운박스 이외의 클릭 이벤트 콜백
     * 숨겨진 input element 에서 onBlur 이벤트 발생
     */
    const handleBlurOnHiddenInput = () => {
        setTimeout(() => {
            if (focus) {
                onBlur();
                setFocus(false);
            }
        }, 200);
    };
    return (jsxs(Select, Object.assign({ width: width }, { children: [jsxs(Wrapper$1, Object.assign({ size: size, isFocus: focus, disabled: disabled, readonly: readonly, error: error, onClick: handleOnClick }, { children: [jsxs("div", { children: [jsxs("div", { children: [!isNil(label) &&
                                        !isNil(value) &&
                                        value !== '' &&
                                        size !== 'tiny' && (jsx(Label, Object.assign({ disabled: disabled, readonly: readonly, error: error }, { children: label }))), jsx("input", { id: name, name: name, ref: ref, readOnly: true, placeholder: value
                                            ? ''
                                            : size === 'tiny'
                                                ? label
                                                : placeholder, inputMode: "none", value: ((_a = find(options, { value: value })) === null || _a === void 0 ? void 0 : _a.label) || '' })] }), jsx("span", Object.assign({ className: "xicon- chevron" }, { children: focus
                                    ? 'chevron_direction_up'
                                    : 'chevron_direction_down' }))] }), jsx(Dropdown, Object.assign({ isFocus: focus, direction: variant, width: width }, { children: jsx("ul", { children: options.map((option, index) => {
                                var _a;
                                return (jsxs(Item, Object.assign({ onClick: e => handleOnItemClick(index) }, { children: [option.leftIcon && (jsx("span", Object.assign({ className: "xicon-" }, { children: option.leftIcon }))), jsxs("div", { children: [jsx("span", Object.assign({ className: "item-label" }, { children: option.label })), (_a = option.captions) === null || _a === void 0 ? void 0 : _a.map((caption, caption_index) => {
                                                    return (jsx("span", Object.assign({ className: "caption-label" }, { children: caption }), caption_index));
                                                })] }), option.rightIcon && (jsx("span", Object.assign({ className: "xicon-" }, { children: option.rightIcon })))] }), index));
                            }) }) })), jsx(HiddenInput, { ref: hiddenInputRef, inputMode: "none", onBlur: handleBlurOnHiddenInput })] })), (caption || error) && (jsxs("div", Object.assign({ css: theme => css `
							padding: 0 16px;
							${theme.font.Caption}
							color: ${error
                    ? theme.color.red[60]
                    : theme.color.mono[60]};
						` }, { children: [caption && !error && jsx("span", { children: caption }), jsx("span", { children: error === null || error === void 0 ? void 0 : error.message })] })))] })));
};
const Select = styled.div `
	width: ${props => (props.width ? `${props.width}px` : '100%')};
	display: flex;
	flex-direction: column;
	gap: 4px;
`;
const Wrapper$1 = styled.div `
	position: relative;
	display: flex;
	height: ${props => props.size === 'default'
    ? '60px'
    : props.size === 'small'
        ? '50px'
        : '32px'};

	border: 1px solid
		${props => !isNil(props.error)
    ? props.theme.color.red[60]
    : props.disabled
        ? props.theme.color.mono[10]
        : props.isFocus
            ? props.theme.color.primary[60]
            : props.theme.color.primary[20]};
	padding: ${props => props.size === 'tiny'
    ? '6px 16px'
    : props.size === 'small'
        ? '6px 12px'
        : '8px 16px'};
	align-items: center;
	background-color: ${props => !isNil(props.error)
    ? props.theme.color.red[10]
    : props.disabled || props.readonly
        ? props.theme.color.primary[5]
        : '#fff'};
	pointer-events: ${props => (props.disabled || props.readonly) && 'none'};
	cursor: pointer;
	.chevron {
		flex-shrink: 0;
		font-size: 24px;
		color: ${props => !isNil(props.error)
    ? props.theme.color.red[60]
    : props.disabled
        ? props.theme.color.mono[20]
        : props.readonly
            ? props.theme.color.primary[20]
            : props.theme.color.black};
	}
	> div:first-of-type {
		width: 100%;
		display: flex;
		gap: 4px;
		align-items: center;
		div {
			flex: 1;
			display: flex;
			flex-direction: column;
			gap: 4px;
		}
		input {
			width: 100%;
			padding: 0;
			border: none;
			outline: none;
			cursor: inherit;
			${props => props.size === 'default'
    ? props.theme.font.Body1
    : props.theme.font.Subtitle2};
			color: ${props => !isNil(props.error)
    ? props.theme.color.red[60]
    : props.disabled
        ? props.theme.color.mono[30]
        : props.readonly
            ? props.theme.color.mono[70]
            : props.theme.color.black};
			background-color: inherit;
			::placeholder {
				color: ${props => !isNil(props.error)
    ? props.theme.color.red[20]
    : props.isFocus
        ? props.theme.color.primary[70]
        : props.disabled
            ? props.theme.color.mono[20]
            : props.readonly
                ? props.theme.color.mono[70]
                : props.theme.color.mono[30]};
			}
		}
	}
`;
const Label = styled.label `
	color: ${props => !isNil(props.error)
    ? props.theme.color.red[60]
    : props.disabled
        ? props.theme.color.mono[30]
        : props.readonly
            ? props.theme.color.mono[70]
            : props.theme.color.primary[90]};
	${props => props.theme.font.Overline};
`;
const Dropdown = styled.div `
	position: absolute;
	z-index: 10;
	left: -1px;
	top: ${props => props.direction === 'dropdown-bottom'
    ? 'calc(100% + 4px)'
    : 'calc(0% - 4px)'};
	width: ${props => (props.width ? `${props.width}px` : '100%')};
	padding: 8px 0px;
	background-color: ${props => props.theme.color.white};
	border: 1px solid ${props => props.theme.color.mono[10]};
	transform: ${props => props.isFocus
    ? props.direction === 'dropdown-bottom'
        ? 'scaleY(1)'
        : 'translateY(-100%) scaleY(1)'
    : props.direction === 'dropdown-bottom'
        ? 'scaleY(0)'
        : 'translateY(-100%) scaleY(0)'};
	transform-origin: ${props => props.direction === 'dropdown-bottom' ? 'top' : 'bottom'};
	transition: transform 0.2s ease;
	ul {
		display: flex;
		flex-direction: column;
		gap: 1px;
	}
`;
const Item = styled.li `
	display: flex;
	align-items: center;
	padding: 12px 16px;
	gap: 8px;
	div {
		flex: 1;
		min-height: 24px;
		display: flex;
		flex-direction: column;
		justify-content: center;
		gap: 4px;
		pointer-events: inherit;
	}
	.item-label {
		color: ${props => props.theme.color.mono[90]};
		${props => props.theme.font.Subtitle2};
		pointer-events: inherit;
	}
	.caption-label {
		color: ${props => props.theme.color.mono[60]};
		${props => props.theme.font.Caption};
	}
	.xicon- {
		font-size: 24px;
		color: ${props => props.theme.color.mono[20]};
	}
	&:hover {
		background-color: ${props => props.theme.color.primary[10]};
		& .item-label {
			color: ${props => props.theme.color.primary[90]};
		}
	}
`;
const HiddenInput = styled.input `
	opacity: 0;
	width: 0;
	padding: 0;
	border: none;
	outline: none;
`;

const ExStepper = ({ name, width, callbackOnChange, callbackOnBlur, }) => {
    const theme = useTheme();
    const { field: { onChange, value }, } = useController({ name });
    const { getValues } = useFormContext();
    const will = useRef(0);
    const handleOnClick = (behavior) => {
        if (behavior === 'increase') {
            will.current = +value + 1;
        }
        else if (behavior === 'decrease') {
            will.current = +value - 1;
        }
        onChange(will.current);
        if (callbackOnChange &&
            will.current !== undefined &&
            +will.current >= 0) {
            callbackOnChange(+will.current);
        }
    };
    return (jsxs(ExStepperBox, Object.assign({ width: width }, { children: [jsx(ExButton, { variant: "contained", size: "tiny", icon: "remove_style_basic", onClick: () => {
                    handleOnClick('decrease');
                }, backgroundColor: theme.color.mono['10'] }), jsx(ExInput, { type: "input", name: name, inputSize: "small", callbackOnBlur: () => {
                    callbackOnBlur && callbackOnBlur(+getValues(name));
                }, numberOnly: true }), jsx(ExButton, { variant: "contained", size: "tiny", icon: "add", onClick: () => {
                    handleOnClick('increase');
                }, backgroundColor: theme.color.mono['10'] })] })));
};
const ExStepperBox = styled.div `
	display: flex;
	width: ${({ width }) => width && width};
	border-radius: 4px;
	height: 32px;
	border: 1px solid ${({ theme }) => theme.color.mono['20']};
	overflow: hidden;

	> div {
		.xicon- {
			color: ${({ theme }) => theme.color.mono['60']};
		}

		&:nth-of-type(odd) {
			height: auto;
		}

		&:nth-of-type(2) {
			> div {
				border: none;
				height: 30px;
				border-left: 1px solid
					${({ theme }) => theme.color.primary['20']};
				border-right: 1px solid
					${({ theme }) => theme.color.primary['20']};
			}
		}
	}

	input {
		text-align: center;
	}
`;

const ExTabs = ({ variant = 'basic', size = 'small', gap = '24px', tabs, setContentsIndex, contentsIndex, disabledTabIndices, backgroundColor, activeBackgroundColor, activeFontColor, hoverFontColor, fontColor, borderColor, isBorder = true, isRouterTabs, direction, onChangeTab, }) => {
    const [tabIndex, setTabIndex] = useState(contentsIndex);
    const handleOnClick = (index) => {
        if (disabledTabIndices === null || disabledTabIndices === void 0 ? void 0 : disabledTabIndices.includes(index)) {
            return;
        }
        if (onChangeTab) {
            onChangeTab(index);
        }
        setTabIndex(index);
        setContentsIndex(index);
    };
    useEffect(() => {
        if (disabledTabIndices) {
            const tabsIndexArray = tabs.map((item, index) => index);
            const difference = tabsIndexArray.filter(index => !disabledTabIndices.includes(index));
            if (onChangeTab) {
                onChangeTab(difference[0]);
            }
            setTabIndex(difference[0]);
            setContentsIndex(difference[0]);
        }
    }, []);
    useEffect(() => {
        if (isRouterTabs) {
            setTabIndex(contentsIndex);
        }
    }, [contentsIndex]);
    return (jsx(ExTabsLayout, Object.assign({ gap: gap, size: size, direction: direction }, { children: tabs === null || tabs === void 0 ? void 0 : tabs.map((tab, index) => variant === 'basic' ? (jsxs(BasicTab, Object.assign({ className: tabIndex === index && tab.className
                ? `active ${tab.className}`
                : tabIndex === index && !tab.className
                    ? `active`
                    : tabIndex !== index && tab.className
                        ? tab.className
                        : (disabledTabIndices === null || disabledTabIndices === void 0 ? void 0 : disabledTabIndices.includes(index))
                            ? 'disabled'
                            : undefined, isBorder: isBorder, borderColor: borderColor, backgroundColor: backgroundColor, activeBackgroundColor: activeBackgroundColor, activeFontColor: activeFontColor, hoverFontColor: hoverFontColor, size: size, fontColor: fontColor, onClick: () => {
                handleOnClick(index);
            } }, { children: [tab.isExistNew && (jsx("span", Object.assign({ className: "xicon-" }, { children: "badge" }))), tab.name] }), index)) : (jsx(UnderLineTab, Object.assign({ className: tabIndex === index && tab.className
                ? `active ${tab.className}`
                : tabIndex === index && !tab.className
                    ? `active`
                    : tabIndex !== index && tab.className
                        ? tab.className
                        : (disabledTabIndices === null || disabledTabIndices === void 0 ? void 0 : disabledTabIndices.includes(index))
                            ? 'disabled'
                            : undefined, size: size, onClick: () => {
                handleOnClick(index);
            } }, { children: tab.name }), index))) })));
};
const ExTabsLayout = styled.div `
	display: flex;
	gap: ${({ gap }) => gap};
	${({ direction }) => direction && `flex-direction:${direction}`};
	flex-wrap: nowrap;
	overflow-x: auto;
	/* width: 100%; */

	> div {
		display: flex;
		justify-content: center;
		align-items: center;
		cursor: pointer;
		white-space: nowrap;
		flex-shrink: 0;
		flex: auto;

		&.disabled {
			cursor: not-allowed;
			color: ${({ theme }) => theme.color.mono['20']};
		}
	}
`;
const BasicTab = styled.div `
	position: relative;
	border-width: ${({ isBorder }) => isBorder && '1px'};
	border-style: solid;
	border-radius: 4px;
	${({ size, theme }) => size === 'small' ? theme.font.Button_s : theme.font.Button_m};
	min-height: ${({ size }) => (size === 'small' ? '36px' : '44px')};
	flex-basis: ${({ size }) => (size === 'small' ? '69px' : '91px')};
	padding: ${({ size }) => (size === 'small' ? '0 24px' : '0 32px')};
	border-color: ${({ borderColor, theme }) => borderColor ? borderColor : theme.color.mono['10']};
	background-color: ${({ backgroundColor, theme }) => backgroundColor ? backgroundColor : theme.color.white};
	color: ${({ fontColor, theme }) => fontColor ? fontColor : theme.color.mono['60']};

	.xicon- {
		position: absolute;
		top: 3px;
		${({ size }) => (size === 'small' ? 'right: 5px' : 'right: 7px')};
		font-size: ${({ size }) => (size === 'small' ? '20px' : '24px')};
		z-index: 1;
		color: ${({ theme }) => theme.color.red['60']};

		:after {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			z-index: -1;
			content: '';
			width: 10px;
			height: 10px;
			border-radius: 50%;
			background-color: #fff;
		}
	}

	:hover {
		color: ${({ hoverFontColor, theme }) => hoverFontColor ? hoverFontColor : theme.color.secondary['60']};
	}

	&.active {
		border-color: ${({ theme }) => theme.color.secondary['60']};
		background-color: ${({ activeBackgroundColor, theme }) => activeBackgroundColor
    ? activeBackgroundColor
    : theme.color.secondary['60']};
		color: ${({ activeFontColor, theme }) => activeFontColor ? activeFontColor : theme.color.white};
	}

	&.disabled {
		background-color: ${({ theme }) => theme.color.white};
		border-color: ${({ theme }) => theme.color.mono['10']};

		.xicon- {
			display: none;
		}
	}
`;
const UnderLineTab = styled.div `
	border-bottom: 2px solid transparent;
	min-height: ${({ size }) => (size === 'small' ? '36px' : '52px')};
	flex-basis: ${({ size }) => (size === 'small' ? '57px' : '135px')};
	padding: ${({ size }) => (size === 'small' ? '0 16px' : '0 48px')};

	${({ size }) => size === 'small'
    ? 'font-size: 13px; font-weight: 500; letter-spacing: 0.001em;'
    : 'font-size: 24px; font-weight: 400;'};

	&:hover {
		border-color: ${({ theme }) => theme.color.primary['30']};
	}

	&.active {
		border-bottom: 2px solid ${({ theme }) => theme.color.primary['60']};
		color: ${({ theme }) => theme.color.primary['60']};
	}

	&.disabled {
		color: ${({ theme }) => theme.color.mono['20']};

		&:hover {
			border-color: transparent;
		}
	}
`;

const ExToast = ({ status, type, message, buttonLabel, device, }) => {
    const theme = useTheme();
    return (jsxs(ExToastLayout, Object.assign({ type: type, status: status, device: device }, { children: [jsx("div", { children: jsx("span", { children: message }) }), buttonLabel && (jsx("div", Object.assign({ css: css `
						display: flex;
						justify-content: end;
					` }, { children: jsx(ExButton, { size: "tiny", variant: "contents", label: buttonLabel, backgroundColor: "inherit", labelColor: status === 'normal'
                        ? `${theme.color.mono[20]}`
                        : status === 'warn'
                            ? `${theme.color.yellow[80]}`
                            : status === 'error'
                                ? `${theme.color.red[80]}`
                                : `${theme.color.green[80]}` }) })))] })));
};
const ExToastLayout = styled.main `
	display: flex;
	flex-direction: ${({ type }) => type === 'double-longer' ? 'column' : 'unset'};
	justify-content: ${({ type }) => type === 'single-none'
    ? 'center'
    : type === 'single-normal' || type === 'double-normal'
        ? 'space-between'
        : 'unset'};
	align-items: ${({ type }) => type === 'double-longer' ? 'unset' : 'center'};
	gap: ${({ type }) => (type === 'double-longer' ? '18px' : 'unset')};
	width: 344px;
	height: ${({ type }) => type === 'single-none' || type === 'single-normal'
    ? '48px'
    : type === 'double-normal'
        ? '68px'
        : '96px'};
	padding: ${({ type }) => type === 'single-none'
    ? '6px 16px'
    : type === 'single-normal' || type === 'double-normal'
        ? '6px 8px 6px 16px'
        : '16px 8px 8px 16px'};
	color: ${({ status, theme }) => status === 'normal'
    ? theme.color.white
    : status === 'warn'
        ? theme.color.yellow[100]
        : status === 'error'
            ? theme.color.red[100]
            : theme.color.green[100]};
	background: ${({ status, theme }) => status === 'normal'
    ? theme.color.mono[90]
    : status === 'warn'
        ? theme.color.yellow[20]
        : status === 'error'
            ? theme.color.red[20]
            : theme.color.green[20]};
	white-space: pre-wrap;
	${({ theme }) => theme.font.Caption}

	${({ theme }) => theme.breakpoints.down('TABLET')} {
		width: ${({ device }) => (device === 'MOBILE' ? '100%' : '344px')};
	}
`;

/**
 * 토글 버튼
 * @param name
 * @param onChange
 * @constructor
 */
const ExToggle = ({ name, onChange }) => {
    const { field: { ref, onChange: onChangeSwitch, value }, } = useController({ name });
    /**
     * 토글 상태 변경 이벤트 콜백 함수
     * @param event
     */
    const handleOnChange = (event) => {
        const { target: { checked }, } = event;
        onChangeSwitch(checked);
        if (!isNil(onChange) && typeof onChange === 'function')
            onChange();
    };
    return (jsxs(Toggle, Object.assign({ checked: value }, { children: [jsx("input", { role: "switch", type: "checkbox", readOnly: true, ref: ref, onChange: handleOnChange }), jsx("span", Object.assign({ className: "xicon-" }, { children: value ? 'on' : 'off' }))] })));
};
/**
 * 토글 버튼
 * - Hook Form 으로 컨트롤 되지 않는 컴포넌트
 */
forwardRef(({ onChange }, ref) => {
    const [checked, setChecked] = useState(false);
    useImperativeHandle(ref, () => ({
        /**
         * 토글 상태 변경 함수
         * @param checked
         */
        setToggle: checked => {
            setChecked(checked);
        },
    }));
    /**
     * 토글 상태 변경 이벤트 콜백 함수
     * @param event
     */
    const handleOnChange = (event) => {
        const { target: { checked }, } = event;
        setChecked(checked);
        if (!isNil(onChange) && typeof onChange === 'function')
            onChange();
    };
    return (jsxs(Toggle, Object.assign({ checked: checked }, { children: [jsx("input", { role: "switch", type: "checkbox", checked: checked, readOnly: true, onChange: handleOnChange }), jsx("span", Object.assign({ className: "xicon-" }, { children: checked ? 'on' : 'off' }))] })));
});
const Toggle = styled.label `
	width: 60px;
	height: 24px;
	display: flex;
	align-items: center;
	padding: 0px 6px;
	border-radius: 100px;
	background-color: ${props => props.checked
    ? props.theme.color.secondary[60]
    : props.theme.color.mono[20]};
	cursor: pointer;
	transition: background-color 0.2s ease;
	input {
		position: absolute;
		opacity: 0;
		width: 0;
		height: 0;
	}
	.xicon- {
		user-select: none;
		color: ${({ theme }) => theme.color.white};
		font-size: 12px;
		line-height: 12px;
		transform: ${props => props.checked ? 'translateX(calc(48px - 100%))' : 'translateX(0)'};
		transition: transform 0.2s ease;
	}
`;

const useResizeObserver = () => {
    const theme = useTheme();
    const [device, setDevice] = useState('DESKTOP');
    let observer;
    if (typeof window !== 'undefined') {
        observer = new ResizeObserver(debounce((entries) => {
            entries.forEach(entry => {
                var _a, _b;
                const width = entry.contentRect.width;
                if (width >= parseInt((_a = theme.breakpoint) === null || _a === void 0 ? void 0 : _a.DESKTOP)) {
                    setDevice('DESKTOP');
                }
                else if (width >= parseInt((_b = theme.breakpoint) === null || _b === void 0 ? void 0 : _b.TABLET)) {
                    setDevice('TABLET');
                }
                else {
                    setDevice('MOBILE');
                }
            });
        }, 100));
        observer.observe(document.body);
    }
    useEffect(() => {
        return () => {
            observer.unobserve(document.body);
        };
    }, []);
    return device;
};

/** custom animation 위한 용도 */
const fade = cssTransition({
    enter: 'Toastify__fade-enter--top-center',
    exit: 'Toastify__fade-exit---top-center',
});
/**toast message 생성 함수 */
const useToastMessage = () => {
    const device = useResizeObserver();
    const showToastMsg = ({ status, type, message, buttonLabel, progress, }) => {
        const defaultOptions = Object.assign(Object.assign({ autoClose: 2000 }, (progress
            ? { progress: 1, closeOnClick: true }
            : { closeOnClick: false })), { pauseOnHover: false, pauseOnFocusLoss: false, hideProgressBar: true, draggable: false, transition: fade });
        toast(jsx(ExToast, { status: status, type: type, message: message, buttonLabel: buttonLabel, device: device }), Object.assign({}, defaultOptions));
    };
    /**ToastContainer 컴포넌트 생성 함수(필수)*/
    const toastContainer = (device) => (jsx(ToastLayout, { children: jsx(ToastContainer, { position: device === 'DESKTOP' ? 'top-center' : 'bottom-center' }) }));
    return { showToastMsg, toastContainer };
};
const ToastLayout = styled.div `
	.Toastify {
		height: 100%;
	}
	.Toastify__toast-container {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}
	:root {
		--toastify-toast-width: 344px;
		--toastify-z-index: 9999;
	}

	.Toastify__toast-container {
		z-index: 999;
		-webkit-transform: translate3d(0, 0, var(--toastify-z-index) px);
		position: fixed;
		box-sizing: border-box;
		color: #fff;
	}
	.Toastify__toast-container--top-left {
		top: 1em;
		left: 1em;
	}
	.Toastify__toast-container--top-center {
		top: 120px;
		left: 50%;
		transform: translateX(-50%);
	}
	.Toastify__toast-container--top-right {
		top: 1em;
		right: 1em;
	}
	.Toastify__toast-container--bottom-left {
		bottom: 1em;
		left: 1em;
	}
	.Toastify__toast-container--bottom-center {
		bottom: 1em;
		left: 50%;
		transform: translateX(-50%);
	}
	.Toastify__toast-container--bottom-right {
		bottom: 1em;
		right: 1em;
	}

	.Toastify__toast {
		position: relative;
		box-sizing: border-box;
		border-radius: 4px;

		/* elevation3 */
		box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.12);
		display: flex;
		justify-content: space-between;
		overflow: hidden;
		cursor: default;
		direction: ltr;
		/* webkit only issue #791 */
		z-index: 0;
	}
	.Toastify__toast--rtl {
		direction: rtl;
	}
	.Toastify__toast--close-on-click {
		cursor: pointer;
	}
	.Toastify__toast-body {
		flex: 1 1 auto;
		display: flex;
		align-items: center;
	}
	.Toastify__toast-body > div:last-child {
		word-break: break-word;
		-ms-flex: 1;
		flex: 1;
		width: 100%;
		height: 100%;
	}
	.Toastify__toast-icon {
		-webkit-margin-end: 10px;
		margin-inline-end: 10px;
		width: 20px;
		-ms-flex-negative: 0;
		flex-shrink: 0;
		display: -ms-flexbox;
		display: flex;
	}

	.Toastify--animate {
		animation-fill-mode: both;
		animation-duration: 0.7s;
	}

	.Toastify--animate-icon {
		animation-fill-mode: both;
		animation-duration: 0.3s;
	}

	@media (max-width: 1199px) {
		.Toastify__toast-container--bottom-center {
			bottom: 16px;
			left: 50%;
		}
	}

	@media (max-width: 767px) {
		.Toastify__toast-container--bottom-center {
			bottom: 16px;
			padding: 0 8px;
			width: 100%;
		}
	}

	.Toastify__close-button {
		/* color: #fff;
	background: transparent;
	outline: none;
	border: none;
	padding: 0;
	cursor: pointer;
	opacity: 0.7;
	transition: 0.3s ease;
	-ms-flex-item-align: start;
	align-self: flex-start; */
	}
	.Toastify__close-button--light {
		/* color: #000;
	opacity: 0.3; */
	}
	.Toastify__close-button > svg {
		/* fill: currentColor;
	height: 16px;
	width: 14px; */
	}
	.Toastify__close-button:hover,
	.Toastify__close-button:focus {
		opacity: 1;
	}

	@keyframes Toastify__trackProgress {
		0% {
			transform: scaleX(1);
		}
		100% {
			transform: scaleX(0);
		}
	}
	//progress-bar 관련 css 지우면 toast 안 사라짐(지우면 안됨!)
	.Toastify__progress-bar {
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		height: 5px;
		z-index: var(--toastify-z-index);
		opacity: 0.7;
		transform-origin: left;
	}
	.Toastify__progress-bar--animated {
		animation: Toastify__trackProgress linear 1 forwards;
	}
	.Toastify__progress-bar--controlled {
		transition: transform 0.2s;
	}
	.Toastify__progress-bar--rtl {
		right: 0;
		left: initial;
		transform-origin: right;
	}

	@keyframes Toastify__bounceInRight {
		from,
		60%,
		75%,
		90%,
		to {
			animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
		}
		from {
			opacity: 0;
			transform: translate3d(3000px, 0, 0);
		}
		60% {
			opacity: 1;
			transform: translate3d(-25px, 0, 0);
		}
		75% {
			transform: translate3d(10px, 0, 0);
		}
		90% {
			transform: translate3d(-5px, 0, 0);
		}
		to {
			transform: none;
		}
	}
	@keyframes Toastify__bounceOutRight {
		20% {
			opacity: 1;
			transform: translate3d(-20px, 0, 0);
		}
		to {
			opacity: 0;
			transform: translate3d(2000px, 0, 0);
		}
	}
	@keyframes Toastify__bounceInLeft {
		from,
		60%,
		75%,
		90%,
		to {
			animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
		}
		0% {
			opacity: 0;
			transform: translate3d(-3000px, 0, 0);
		}
		60% {
			opacity: 1;
			transform: translate3d(25px, 0, 0);
		}
		75% {
			transform: translate3d(-10px, 0, 0);
		}
		90% {
			transform: translate3d(5px, 0, 0);
		}
		to {
			transform: none;
		}
	}
	@keyframes Toastify__bounceOutLeft {
		20% {
			opacity: 1;
			transform: translate3d(20px, 0, 0);
		}
		to {
			opacity: 0;
			transform: translate3d(-2000px, 0, 0);
		}
	}
	@keyframes Toastify__bounceInUp {
		from,
		60%,
		75%,
		90%,
		to {
			animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
		}
		from {
			opacity: 0;
			transform: translate3d(0, 3000px, 0);
		}
		60% {
			opacity: 1;
			transform: translate3d(0, -20px, 0);
		}
		75% {
			transform: translate3d(0, 10px, 0);
		}
		90% {
			transform: translate3d(0, -5px, 0);
		}
		to {
			transform: translate3d(0, 0, 0);
		}
	}
	@keyframes Toastify__bounceOutUp {
		20% {
			transform: translate3d(0, -10px, 0);
		}
		40%,
		45% {
			opacity: 1;
			transform: translate3d(0, 20px, 0);
		}
		to {
			opacity: 0;
			transform: translate3d(0, -2000px, 0);
		}
	}
	@keyframes Toastify__bounceInDown {
		from,
		60%,
		75%,
		90%,
		to {
			animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
		}
		0% {
			opacity: 0;
			transform: translate3d(0, -3000px, 0);
		}
		60% {
			opacity: 1;
			transform: translate3d(0, 25px, 0);
		}
		75% {
			transform: translate3d(0, -10px, 0);
		}
		90% {
			transform: translate3d(0, 5px, 0);
		}
		to {
			transform: none;
		}
	}
	@keyframes Toastify__bounceOutDown {
		20% {
			transform: translate3d(0, 10px, 0);
		}
		40%,
		45% {
			opacity: 1;
			transform: translate3d(0, -20px, 0);
		}
		to {
			opacity: 0;
			transform: translate3d(0, 2000px, 0);
		}
	}
	.Toastify__bounce-enter--top-left,
	.Toastify__bounce-enter--bottom-left {
		animation-name: Toastify__bounceInLeft;
	}
	.Toastify__bounce-enter--top-right,
	.Toastify__bounce-enter--bottom-right {
		animation-name: Toastify__bounceInRight;
	}
	.Toastify__bounce-enter--top-center {
		animation-name: Toastify__bounceInDown;
	}
	.Toastify__bounce-enter--bottom-center {
		animation-name: Toastify__bounceInUp;
	}

	.Toastify__bounce-exit--top-left,
	.Toastify__bounce-exit--bottom-left {
		animation-name: Toastify__bounceOutLeft;
	}
	.Toastify__bounce-exit--top-right,
	.Toastify__bounce-exit--bottom-right {
		animation-name: Toastify__bounceOutRight;
	}
	.Toastify__bounce-exit--top-center {
		animation-name: Toastify__bounceOutUp;
	}
	.Toastify__bounce-exit--bottom-center {
		animation-name: Toastify__bounceOutDown;
	}

	@keyframes Toastify__zoomIn {
		from {
			opacity: 0;
			transform: scale3d(0.3, 0.3, 0.3);
		}
		50% {
			opacity: 1;
		}
	}
	@keyframes Toastify__zoomOut {
		from {
			opacity: 1;
		}
		50% {
			opacity: 0;
			transform: scale3d(0.3, 0.3, 0.3);
		}
		to {
			opacity: 0;
		}
	}
	.Toastify__zoom-enter {
		animation-name: Toastify__zoomIn;
	}

	.Toastify__zoom-exit {
		animation-name: Toastify__zoomOut;
	}

	@keyframes Toastify__flipIn {
		from {
			transform: perspective(400px) rotate3d(1, 0, 0, 90deg);
			animation-timing-function: ease-in;
			opacity: 0;
		}
		40% {
			transform: perspective(400px) rotate3d(1, 0, 0, -20deg);
			animation-timing-function: ease-in;
		}
		60% {
			transform: perspective(400px) rotate3d(1, 0, 0, 10deg);
			opacity: 1;
		}
		80% {
			transform: perspective(400px) rotate3d(1, 0, 0, -5deg);
		}
		to {
			transform: perspective(400px);
		}
	}
	@keyframes Toastify__flipOut {
		from {
			transform: perspective(400px);
		}
		30% {
			transform: perspective(400px) rotate3d(1, 0, 0, -20deg);
			opacity: 1;
		}
		to {
			transform: perspective(400px) rotate3d(1, 0, 0, 90deg);
			opacity: 0;
		}
	}
	.Toastify__flip-enter {
		animation-name: Toastify__flipIn;
	}

	.Toastify__flip-exit {
		animation-name: Toastify__flipOut;
	}

	@keyframes Toastify__slideInRight {
		from {
			transform: translate3d(110%, 0, 0);
			visibility: visible;
		}
		to {
			transform: translate3d(0, 0, 0);
		}
	}
	@keyframes Toastify__slideInLeft {
		from {
			transform: translate3d(-110%, 0, 0);
			visibility: visible;
		}
		to {
			transform: translate3d(0, 0, 0);
		}
	}
	@keyframes Toastify__slideInUp {
		from {
			transform: translate3d(0, 110%, 0);
			visibility: visible;
		}
		to {
			transform: translate3d(0, 0, 0);
		}
	}
	@keyframes Toastify__slideInDown {
		from {
			transform: translate3d(0, -110%, 0);
			visibility: visible;
		}
		to {
			transform: translate3d(0, 0, 0);
		}
	}
	@keyframes Toastify__slideOutRight {
		from {
			transform: translate3d(0, 0, 0);
		}
		to {
			visibility: hidden;
			transform: translate3d(110%, 0, 0);
		}
	}
	@keyframes Toastify__slideOutLeft {
		from {
			transform: translate3d(0, 0, 0);
		}
		to {
			visibility: hidden;
			transform: translate3d(-110%, 0, 0);
		}
	}
	@keyframes Toastify__slideOutDown {
		from {
			transform: translate3d(0, 0, 0);
		}
		to {
			visibility: hidden;
			transform: translate3d(0, 500px, 0);
		}
	}
	@keyframes Toastify__slideOutUp {
		from {
			transform: translate3d(0, 0, 0);
		}
		to {
			visibility: hidden;
			transform: translate3d(0, -500px, 0);
		}
	}
	.Toastify__slide-enter--top-left,
	.Toastify__slide-enter--bottom-left {
		animation-name: Toastify__slideInLeft;
	}
	.Toastify__slide-enter--top-right,
	.Toastify__slide-enter--bottom-right {
		animation-name: Toastify__slideInRight;
	}
	.Toastify__slide-enter--top-center {
		animation-name: Toastify__slideInDown;
	}
	.Toastify__slide-enter--bottom-center {
		animation-name: Toastify__slideInUp;
	}

	.Toastify__slide-exit--top-left,
	.Toastify__slide-exit--bottom-left {
		animation-name: Toastify__slideOutLeft;
	}
	.Toastify__slide-exit--top-right,
	.Toastify__slide-exit--bottom-right {
		animation-name: Toastify__slideOutRight;
	}
	.Toastify__slide-exit--top-center {
		animation-name: Toastify__slideOutUp;
	}
	.Toastify__slide-exit--bottom-center {
		animation-name: Toastify__slideOutDown;
	}

	@keyframes Toastify__spin {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}

	/*# sourceMappingURL=ReactToastify.css.map */

	.Toastify__fade-enter--top-center {
		animation: Toastify__fadeIn 1s both;
	}

	.Toastify__fade-exit---top-center {
		animation: Toastify__fadeOut 1s both;
	}

	@keyframes Toastify__fadeIn {
		from {
			opacity: 0;
		}
		to% {
			opacity: 1;
		}
	}

	@keyframes Toastify__fadeOut {
		from {
			opacity: 1;
		}

		to {
			opacity: 0;
		}
	}
`;

const ExTooltip = ({ message, messagePosition }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef(null);
    const device = useResizeObserver();
    const isDeskTop = device === 'DESKTOP';
    const handleOpenToolTip = () => {
        setIsOpen(true);
    };
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    useEffect(() => {
        if (isOpen && isDeskTop) {
            setIsOpen(false);
        }
    }, [device]);
    return (jsxs(ExTooltipLayout, Object.assign({ onClick: handleOpenToolTip }, { children: [jsx("span", Object.assign({ className: "xicon-" }, { children: "tooltip" })), jsx(ExTooltipMessageBox, Object.assign({ messagePosition: messagePosition, isOpen: isOpen, isDeskTop: isDeskTop, ref: ref }, { children: message }))] })));
};
const ExTooltipLayout = styled.div `
	position: relative;
	width: fit-content;

	.xicon- {
		font-size: 20px;
		cursor: default;
		color: ${({ theme }) => theme.color.mono['80']};

		:hover {
			& + div {
				display: block;
			}
		}
	}
`;
const ExTooltipMessageBox = styled.div `
	position: absolute;
	display: ${({ isOpen, isDeskTop }) => isOpen === true && !isDeskTop ? 'block' : 'none'};
	${({ messagePosition }) => messagePosition === 'leftBottom'
    ? 'left: 0; top: calc(100% + 4px);'
    : messagePosition === 'rightBottom'
        ? 'right:0; top: calc(100% + 4px);'
        : messagePosition === 'leftTop'
            ? 'left:0; bottom: calc(100% + 4px);'
            : messagePosition === 'rightTop' &&
                'right:0; bottom: calc(100% + 4px);'};
	padding: 16px;
	width: 272px;
	border-radius: 8px;
	box-shadow: ${({ theme }) => theme.elevation.elevation3};
	z-index: 1;
	background-color: ${({ theme }) => theme.color.black};
	${({ theme }) => theme.font.Caption};
	color: ${({ theme }) => theme.color.white};
`;

const Popup = ({ variant = 'basic', title, subtitle, info, onlyConfirm = false, confirmLabel = '예', cancelLabel = '아니오', buttonColor, icon, iconColor, titlePrewrap, titleTextAlign, cancelClassName, closeIconClassName, confirmClassName, }) => {
    useTheme();
    const { close } = useContext(DialogContext);
    const iconRef = useRef(icon ||
        (_variant => {
            let result;
            switch (_variant) {
                case 'icon':
                case 'request':
                    result = 'helpdesk';
                    break;
                case 'done':
                    result = 'done_all';
                    break;
                case 'warn':
                case 'error':
                    result = 'attention';
                    break;
            }
            return result;
        })(variant));
    const handleOnConfirm = (e) => {
        e.stopPropagation();
        close('confirm');
    };
    const handleOnCancel = (e) => {
        e.stopPropagation();
        close('cancel');
    };
    return (jsxs("div", Object.assign({ role: "dialog", css: theme => css `
				width: 328px;
				z-index: 1001;
				display: flex;
				flex-direction: column;
				gap: 24px;
				padding: 40px 0 0;
				overflow: hidden;
				background-color: ${theme.color.white};
				border-radius: 8px;
				${theme.elevation.elevation4}
			` }, { children: [iconRef.current && (jsx(Icon, Object.assign({ iconColor: iconColor, className: "xicon-" }, { children: iconRef.current }))), jsxs("div", Object.assign({ css: css `
					display: flex;
					flex-direction: column;
					gap: 24px;
					padding: 0 16px 16px;
				` }, { children: [jsxs(Title$1, Object.assign({ titlePrewrap: titlePrewrap, titleTextAlign: titleTextAlign }, { children: [jsx("span", Object.assign({ className: "title" }, { children: title })), subtitle && jsx("span", Object.assign({ className: "subtitle" }, { children: subtitle }))] })), variant === 'info' && jsx(Info, { children: info })] })), jsxs("div", Object.assign({ css: css `
					display: flex;
					flex-direction: column;
				` }, { children: [jsx("div", { css: theme => css `
						height: 1px;
						background-color: ${theme.color.mono[10]};
					` }), onlyConfirm && (jsx(ExButton, { variant: "contents", size: "xLarge", label: confirmLabel, labelColor: buttonColor, onClick: handleOnConfirm })), !onlyConfirm && (jsxs("div", Object.assign({ css: css `
							display: flex;
							> div {
								flex: 1;
							}
						` }, { children: [jsx(ExButton, { variant: "contents", size: "xLarge", label: cancelLabel, labelColor: buttonColor, onClick: handleOnCancel }), jsx(ExButton, { variant: "contained", size: "xLarge", label: confirmLabel, backgroundColor: buttonColor, onClick: handleOnConfirm })] })))] }))] })));
};
const Title$1 = styled.div `
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
	> .title {
		${props => props.theme.font.Title2_h};
		white-space: ${({ titlePrewrap }) => titlePrewrap ? 'pre-wrap' : 'unset'};
		text-align: ${({ titleTextAlign }) => titleTextAlign ? titleTextAlign : 'unset'};
	}
	> .subtitle {
		color: ${props => props.theme.color.mono[60]};
		${props => props.theme.font.Subtitle2};
	}
`;
const Icon = styled.span `
	font-size: 72px;
	line-height: 72px;
	color: ${props => props.iconColor || props.theme.color.primary[60]};
	margin: auto;
`;
const Info = styled.div `
	padding: 16px;
	background: ${props => props.theme.color.primary[5]};
	border-radius: 8px;
	color: ${props => props.theme.color.mono[60]};
	${props => props.theme.font.Subtitle2};
`;

const DialogContext = createContext({
    openPopup: (options) => {
        /* default open popup function */
    },
    openModal: (options) => {
        /* default open modal function*/
    },
    close: (reason) => {
        /* default close function */
    },
});
const DialogContextProvider = ({ children, }) => {
    const [dialogs, setDialogs] = useState([]);
    /**
     * 팝업 or 모달 Dialog 생성
     */
    const createDialog = (props) => {
        if (props.variant !== undefined) {
            setDialogs([...dialogs, Object.assign(Object.assign({}, props), { isOpen: true, type: 'popup' })]);
        }
        else {
            setDialogs([...dialogs, Object.assign(Object.assign({}, props), { isOpen: true, type: 'modal' })]);
        }
    };
    /**
     * 생성된 (팝업 or 모달) Dialog 의 제거(닫힘)
     * @param reason
     */
    const closeDialog = (reason) => {
        setDialogs(dialogs => {
            const latestDialog = dialogs.pop();
            if (isNil(latestDialog))
                return dialogs;
            if (latestDialog.type === 'popup') {
                const { onConfirm, onCancel } = latestDialog;
                if (reason === 'confirm' && onConfirm)
                    onConfirm();
                if (reason === 'cancel' && onCancel)
                    onCancel();
            }
            if (latestDialog.type === 'modal') {
                const { onPrimaryClick, onSecondaryClick } = latestDialog;
                if (reason === 'primary' && onPrimaryClick)
                    onPrimaryClick();
                if (reason === 'secondary' && onSecondaryClick)
                    onSecondaryClick();
            }
            return [...dialogs].concat(Object.assign(Object.assign({}, latestDialog), { isOpen: false }));
        });
        setDialogs(dialogs => dialogs.slice(0, dialogs.length - 1));
    };
    const handleOnDimClick = (dialog) => {
        if (isNil(dialog))
            return;
        if ((dialog === null || dialog === void 0 ? void 0 : dialog.type) === 'popup')
            return;
        closeDialog('dim');
    };
    const contextValue = useRef({
        openPopup: createDialog,
        openModal: createDialog,
        close: closeDialog,
    });
    useEffect(() => {
        const isOepn = dialogs.find(dialog => dialog.isOpen === true);
        if (isOepn) {
            document.body.style.overflow = 'hidden';
        }
        else {
            document.body.style.overflow = 'unset';
        }
    }, [dialogs]);
    return (jsxs(DialogContext.Provider, Object.assign({ value: contextValue.current }, { children: [children, jsxs("div", Object.assign({ role: "presentation", css: css `
					position: fixed;
					z-index: 9999;
					display: flex;
					align-items: center;
					justify-content: center;
					${isEmpty(dialogs) ? '' : 'inset: 0px'};
				` }, { children: [jsx("div", { "aria-hidden": true, css: css `
						position: fixed;
						width: ${isEmpty(dialogs) ? 0 : '100%'};
						height: ${isEmpty(dialogs) ? 0 : '100%'};
						opacity: ${isEmpty(dialogs) ? 0 : 1};
						transition: opacity 225ms cubic-bezier(0.4, 0, 0.2, 1);
						background-color: rgba(0, 0, 0, 0.5);
					`, onClick: () => handleOnDimClick(dialogs[dialogs.length - 1]) }), dialogs.map((dialog, idx) => {
                        return (jsxs(Fragment$1, { children: [dialog.type === 'popup' && dialog.isOpen && (jsx(Popup, { variant: dialog.variant, title: dialog.title, subtitle: dialog.subtitle, info: dialog.info, onlyConfirm: dialog.onlyConfirm, confirmLabel: dialog
                                        .confirmLabel, cancelLabel: dialog.cancelLabel, buttonColor: dialog.buttonColor, icon: dialog.icon, iconColor: dialog.iconColor, titlePrewrap: dialog
                                        .titlePrewrap, titleTextAlign: dialog
                                        .titleTextAlign })), dialog.type === 'modal' && dialog.isOpen && (jsx(Modal, Object.assign({ closeIconClassName: dialog
                                        .closeIconClassName, primaryClassName: dialog
                                        .primaryClassName, secondaryClassName: dialog
                                        .secondaryClassName, title: dialog.title, caption: dialog.caption, primaryLabel: dialog
                                        .primaryLabel, secondaryLabel: dialog
                                        .secondaryLabel, onPrimaryClick: dialog
                                        .onPrimaryClick, onSecondaryClick: dialog
                                        .onSecondaryClick, overflow: dialog.overflow }, { children: dialog.children })))] }, idx));
                    })] }))] })));
};

const Modal = ({ title, caption, children, primaryLabel, secondaryLabel, overflow, closeIconClassName, primaryClassName, secondaryClassName, }) => {
    const { close } = useContext(DialogContext);
    const handleOnClose = () => {
        close('cancel');
    };
    const handleOnPrimaryCtaClick = (e) => {
        e.stopPropagation();
        close('primary');
    };
    const handleOnSecondaryCtaClick = (e) => {
        e.stopPropagation();
        close('secondary');
    };
    return (jsxs("div", Object.assign({ role: "dialog", css: theme => css `
					width: 920px;
					height: 700px;
					z-index: 1001;
					display: flex;
					flex-direction: column;
					gap: 24px;
					padding: 32px;
					overflow: hidden;
					background-color: ${theme.color.white};
					border-radius: 16px;
					${theme.elevation.elevation4};
				` }, { children: [jsxs(Title, { children: [jsxs("div", Object.assign({ css: theme => css `
						display: flex;
						gap: 16px;
						.xicon-wrap {
							height: fit-content;
							cursor: pointer;
						}
						.xicon- {
							font-size: 24px;
							line-height: 24px;
							color: ${theme.color.mono[20]};
						}
					` }, { children: [jsxs("div", Object.assign({ css: theme => css `
								flex: 1;
								display: flex;
								flex-direction: column;
								padding: 0 0 0 8px;
								gap: 8px;
								> .title {
									color: ${theme.color.black};
									${theme.font.Title2_h};
								}
								> .caption {
									color: ${theme.color.mono[60]};
									${theme.font.Caption};
								}
							` }, { children: [jsx("span", Object.assign({ className: "title" }, { children: title })), jsx("span", Object.assign({ className: "caption" }, { children: caption }))] })), jsx("div", Object.assign({ className: `xicon-wrap ${closeIconClassName}`, role: "button", tabIndex: 0, css: css `
							display: flex;
						`, onKeyDown: handleOnClose, onClick: handleOnClose }, { children: jsx("span", Object.assign({ className: "xicon-" }, { children: "close_style_border" })) }))] })), jsx("div", { css: theme => css `
							width: 100%;
							height: 1px;
							background-color: ${theme.color.mono[10]};
						` })] }), jsx("section", Object.assign({ css: css `
					flex: 1;
					overflow-y: ${overflow ? overflow : 'unset'};
				` }, { children: children })), jsxs("section", Object.assign({ css: css `
					display: flex;
					justify-content: ${isNil(secondaryLabel)
                    ? 'center'
                    : 'space-between'};
				` }, { children: [!isNil(secondaryLabel) && (jsx(ExButton, { className: secondaryClassName, variant: 'contents', size: 'xLarge', label: secondaryLabel, onClick: handleOnSecondaryCtaClick })), jsx(ExButton, { className: primaryClassName, variant: 'contained', size: 'xLarge', label: primaryLabel, width: isNil(secondaryLabel) ? 185 : 106, onClick: handleOnPrimaryCtaClick })] }))] })));
};
const Title = styled.div `
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const corporationInfo = [
    {
        item: '주소',
        info: '경기도 고양시 덕양구 향기로 152 에너지엑스 DY빌딩',
    },
    {
        item: '사업자등록번호',
        info: '736-86-01306',
    },
    {
        item: '대표이사',
        info: '박성현 홍두화',
    },
    {
        item: '고객센터',
        info: '02-554-9114',
    },
];
const snsLink = [
    {
        sns: 'youtube',
        logo: 'https://s3.ap-northeast-2.amazonaws.com/energyx.file/assets/logos/youtube_logo.png',
        link: 'https://www.youtube.com/channel/UCsWfQHJu8LiyPX9emfkjJrA',
    },
    {
        sns: 'naver',
        logo: 'https://s3.ap-northeast-2.amazonaws.com/energyx.file/assets/logos/naver_logo.png',
        link: 'https://blog.naver.com/energyx_official',
    },
    {
        sns: 'facebook',
        logo: 'https://s3.ap-northeast-2.amazonaws.com/energyx.file/assets/logos/facebook_logo.png',
        link: 'https://www.facebook.com/energyx.official',
    },
    {
        sns: 'instagram',
        logo: 'https://s3.ap-northeast-2.amazonaws.com/energyx.file/assets/logos/instagram_logo.png',
        link: 'https://www.instagram.com/energyx_official/',
    },
    {
        sns: 'kakaochannel',
        logo: 'https://s3.ap-northeast-2.amazonaws.com/energyx.file/assets/logos/kakaochannel_logo.png',
        link: 'https://pf.kakao.com/_JeRPxb',
    },
];
const createTermsUrl = (deployEnv) => {
    switch (deployEnv) {
        case 'stage':
            return 'https://stage-member.energyx.ai/terms?fetchfrom=ENERGYX&tabValue=';
        case 'main':
            return 'https://member.energyx.ai/terms?fetchfrom=ENERGYX&tabValue=';
        default:
            return 'https://dev-member.energyx.co.kr/terms?fetchfrom=ENERGYX&tabValue=';
    }
};
const Footer = ({ deployEnv }) => {
    return (jsx("footer", Object.assign({ css: theme => css `
				padding: 40px 24px;
				border-top: 1px solid ${theme.color.primary['20']};
				display: flex;
				justify-content: center;

				${theme.breakpoints.down('DESKTOP')} {
					padding: 40px 16px;
				}

				${theme.breakpoints.down('TABLET')} {
					padding: 32px 8px;
				}
			` }, { children: jsxs("div", Object.assign({ css: theme => css `
					width: 1440px;

					@media (max-width: 1440px) {
						width: 100%;

						${theme.breakpoints.down('DESKTOP')} {
							padding: 0 8px;
						}
					}
				` }, { children: [jsxs(FooterTopBox, { children: [jsx(ExButton, { variant: "contents", size: "tiny", label: "\uC11C\uBE44\uC2A4 \uC774\uC6A9\uC57D\uAD00", onClick: () => window.open(`${createTermsUrl(deployEnv)}PRIVACY_USE_AGREEMENT`) }), jsx(ExButton, { variant: "contents", size: "tiny", label: "\uAC1C\uC778\uC815\uBCF4 \uCC98\uB9AC\uBC29\uCE68", onClick: () => window.open(`${createTermsUrl(deployEnv)}PRIVACY_POLICY`) }), jsx(ExButton, { variant: "contents", size: "tiny", label: "\uC0AC\uC774\uD2B8 \uC774\uC6A9\uC548\uB0B4", onClick: () => window.open('https://guide.energyx.ai/') }), jsx(ExButton, { variant: "contents", size: "tiny", label: "\uC778\uC7AC \uC601\uC785", onClick: () => window.open('https://nrgx.notion.site/e9be444910534fbc9446feac61204332') })] }), jsxs(FooterBottomBox, { children: [jsx("div", { children: corporationInfo.map((info, index) => (jsxs("div", { children: [jsx("div", { children: info.item }), jsx("i", {}), jsx("span", { children: info.info })] }, index))) }), jsxs("div", { children: [jsx("div", { children: snsLink.map((sns, index) => (jsx("div", Object.assign({ onClick: () => window.open(sns.link), "aria-hidden": true }, { children: jsx("img", { src: sns.logo, alt: "sns\uB85C\uACE0" }) }), index))) }), jsx("img", { src: "https://s3.ap-northeast-2.amazonaws.com/energyx.file/assets/logos/energyx-logo.svg", alt: "\uC5D0\uB108\uC9C0\uC5D1\uC2A4 \uB85C\uACE0" })] })] })] })) })));
};
const FooterTopBox = styled.div `
	padding-bottom: 24px;
	display: flex;
	gap: 8px;
	justify-content: center;
	align-items: center;
	flex-wrap: wrap;
	${({ theme }) => theme.font.Button_s};

	${({ theme }) => theme.breakpoints.down('DESKTOP')} {
		padding-bottom: 16px;
		justify-content: flex-start;
		margin-left: -8px;
	}

	> div {
		label {
			color: ${({ theme }) => theme.color.mono['70']};
		}
	}

	> i {
		width: 1px;
		height: 14px;
		background-color: ${({ theme }) => theme.color.primary['10']};
	}
`;
const FooterBottomBox = styled.div `
	padding-top: 24px;
	border-top: 1px solid ${({ theme }) => theme.color.primary['10']};
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	gap: 24px;

	${({ theme }) => theme.breakpoints.down('DESKTOP')} {
		flex-direction: column;
		align-items: flex-start;
		gap: 32px;
	}

	> div:first-of-type {
		display: flex;
		gap: 32px;

		${({ theme }) => theme.breakpoints.down('DESKTOP')} {
			flex-direction: column;
			gap: 8px;
		}

		> div {
			display: flex;
			align-items: center;
			gap: 12px;

			> div {
				${({ theme }) => theme.font.Caption_h};
				color: ${({ theme }) => theme.color.mono['70']};
			}

			> i {
				width: 1px;
				height: 14px;
				background-color: ${({ theme }) => theme.color.mono['20']};

				${({ theme }) => theme.breakpoints.down('DESKTOP')} {
					background-color: ${({ theme }) => theme.color.mono['10']};
				}
			}

			> span {
				font-weight: 400;
				font-size: 12px;
				line-height: 18px;
				letter-spacing: 0.003em;
				color: ${({ theme }) => theme.color.mono['70']};
			}
		}
	}

	> div:nth-of-type(2) {
		display: flex;
		align-items: center;
		gap: 24px;

		${({ theme }) => theme.breakpoints.down('DESKTOP')} {
			width: 100%;
			align-items: center;
			justify-content: space-between;
		}

		> div {
			display: flex;
			gap: 20px;

			${({ theme }) => theme.breakpoints.down('DESKTOP')} {
				gap: 8px;
			}
		}

		> img {
			width: 113px;
			height: 17px;
		}
	}
`;

const getHref = (isHttps, url, port) => {
    let href = `${isHttps ? 'https:' : 'http:'}`;
    if (isNil$1(url)) {
        if (isNil$1(port))
            href += `${window.location.hostname}`;
        else
            href += `${window.location.hostname}:${port}`;
    }
    else {
        if (isNil$1(port))
            href += `${url}`;
        else
            href += `${url}:${port}`;
    }
    return href;
};

const handleOnClickTitleForNavigate = ({ lnb, navigate, }) => {
    if (lnb.openNewWindow) {
        window.open(lnb.path);
    }
    else {
        if (lnb.path)
            navigate(lnb.path);
    }
};

const LnbMenu$1 = ({ display, left, lnbSection, userInfo, handleDisplay, }) => {
    const navigate = useNavigate();
    const handleOnClickLnbTitle = (lnb) => {
        handleOnClickTitleForNavigate({
            lnb,
            navigate,
        });
        handleDisplay(false, -1);
    };
    return (jsx("ul", Object.assign({ css: theme => css `
				position: absolute;
				top: 72px;
				left: ${left}px;
				display: ${display ? 'flex' : 'none'};
				flex-direction: column;
				gap: 24px;
				padding: 40px;
				background-color: ${userInfo.isPartner
            ? theme.color.primary[60]
            : theme.color.white};
				border: 1px solid
					${userInfo.isPartner
            ? theme.color.primary[40]
            : theme.color.mono[5]};
				border-radius: 12px;
				${theme.elevation.elevation1};
			`, onMouseLeave: () => handleDisplay(false, -1) }, { children: jsx("div", Object.assign({ className: "lnb-section", css: css `
					display: flex;
					flex-direction: column;
					gap: 24px;
				` }, { children: lnbSection === null || lnbSection === void 0 ? void 0 : lnbSection.map((section, index) => {
                return (jsxs(Fragment$1, { children: [jsxs(SectionMenu, Object.assign({ isPartner: userInfo.isPartner }, { children: [!isNil(section.sectionTitle) && (jsx("span", { children: section.sectionTitle })), section.lnb.map((lnb, lnbIndex) => {
                                    var _a;
                                    return (jsxs(Menu, Object.assign({ isPartner: userInfo.isPartner }, { children: [jsxs("div", { children: [jsx("span", Object.assign({ role: "presentation", onClick: () => handleOnClickLnbTitle(lnb) }, { children: lnb.title })), jsx("div", Object.assign({ className: "hashtags" }, { children: (_a = lnb.hashtag) === null || _a === void 0 ? void 0 : _a.map((tag, tagIndex) => {
                                                            return (jsx(ExLabelTag, { label: tag, variant: "default" }, tagIndex));
                                                        }) }))] }), jsx("span", { children: lnb.description })] }), lnbIndex));
                                })] })), index !== (lnbSection === null || lnbSection === void 0 ? void 0 : lnbSection.length) - 1 && (jsx("div", { css: theme => css `
											height: 1px;
											background-color: ${theme.color
                                .mono[10]};
										` }))] }, index));
            }) })) })));
};
const SectionMenu = styled.div `
	display: flex;
	flex-direction: column;
	gap: 24px;
	padding: 4px 0;
	> span {
		color: ${props => props.isPartner
    ? props.theme.color.primary[20]
    : props.theme.color.mono[60]};
		${props => props.theme.font.Body2};
	}
`;
const Menu = styled.li `
	display: flex;
	flex-direction: column;
	gap: 8px;
	> div {
		display: flex;
		gap: 8px;
		align-items: center;
		> span {
			cursor: pointer;
			color: ${props => props.isPartner
    ? props.theme.color.white
    : props.theme.color.black};
			${props => props.theme.font.Subtitle};
			:hover {
				color: ${props => props.isPartner
    ? props.theme.color.white
    : props.theme.color.primary[60]};
				${props => (props.isPartner ? { fontWeight: 700 } : {})}
			}
		}
		> div {
			display: flex;
			gap: 8px;
			> span {
				border: ${props => props.isPartner
    ? `1px solid ${props.theme.color.primary[20]}`
    : `1px solid ${props.theme.color.mono[10]}`};
				color: ${props => props.isPartner
    ? props.theme.color.primary[5]
    : props.theme.color.mono[60]};
			}
		}
	}
	> span {
		color: ${props => props.isPartner
    ? props.theme.color.primary[30]
    : props.theme.color.mono[40]};
		${props => props.theme.font.Caption};
	}
`;

const MobileNav = ({ display, gnb, auth, userInfo, handleOnClickLogin, onLogout, handleDisplay, handleOnSignup, notificationCallback, }) => {
    const theme = useTheme();
    const [gnbIndex, setGnbIndex] = useState(-1);
    const navigate = useNavigate();
    const handleOnClickGnb = (event, onGnb) => {
        event.stopPropagation();
        const { title, hasLnb, path } = onGnb;
        if (!hasLnb) {
            navigate(path);
            handleDisplay(false);
            return;
        }
        const gnbIndex = findIndex(gnb, { title });
        setGnbIndex(prevState => {
            if (prevState === gnbIndex) {
                return -1;
            }
            // handleDisplay(false);
            return gnbIndex;
        });
    };
    const handleOnLogout = () => {
        if (!isNil(onLogout))
            onLogout();
    };
    const handleOnClickLnbTitle = (lnb) => {
        handleOnClickTitleForNavigate({
            lnb,
            navigate,
        });
        handleDisplay(false);
    };
    return (jsxs("div", Object.assign({ role: "presentation", css: css `
				position: fixed;
				z-index: 1000;
				display: flex;

				${display ? 'inset: 0px' : ''};
			` }, { children: [jsx("div", { "aria-hidden": true, css: css `
					position: fixed;
					width: ${display ? '100%' : 0};
					height: ${display ? '100%' : 0};
					opacity: ${display ? 1 : 0};
					background-color: rgba(0, 0, 0, 0.5);
					transition: opacity 225ms cubic-bezier(0.4, 0, 0.2, 1);
				`, onClick: () => handleDisplay(false) }), jsxs("div", Object.assign({ className: "floating", css: css `
					display: flex;
					height: 100%;
					position: fixed;
					right: 0;
					transform-origin: right;
					transform: ${display
                    ? 'translateX(0)'
                    : 'translateX(100%)'};
					transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
				` }, { children: [jsxs(CloseButton, Object.assign({ onClick: () => handleDisplay(false) }, { children: [jsx(ExButton, { variant: 'contents', size: 'xLarge', icon: 'close_style_border', iconColor: '#fff', backgroundColor: "inherit" }), jsx(ExButton, { variant: 'contents', size: 'small', icon: 'close_style_border', iconColor: '#fff', backgroundColor: "inherit" })] })), jsxs(Box, { children: [auth ? (jsx(Member$1, { children: jsxs("div", Object.assign({ css: theme => css `
									display: flex;
									justify-content: space-between;
									align-items: center;
									padding: 0 8px;
									gap: 12px;
									div:nth-of-type(2) {
										display: none;
									}
									div:nth-of-type(4) {
										display: none;
									}
									${theme.breakpoints.down('TABLET')} {
										padding: 0;
										div:nth-of-type(1) {
											display: none;
										}
										div:nth-of-type(2) {
											display: flex;
										}
										div:nth-of-type(3) {
											display: none;
										}
										div:nth-of-type(4) {
											display: flex;
										}
									}
								` }, { children: [jsx(ExButton, { variant: 'contents', size: 'xLarge', label: userInfo.name, icon: 'person_style_filled', iconPosition: 'left' }), jsx(ExButton, { variant: 'contents', size: 'medium', label: userInfo.name, icon: 'person_style_filled', iconPosition: 'left' }), jsx(ExButton, { variant: 'contents', size: 'large', label: '로그아웃', labelColor: theme.color.mono[60], onClick: () => {
                                                handleDisplay(false);
                                                handleOnLogout();
                                            } }), jsx(ExButton, { variant: 'contents', size: 'small', label: '로그아웃', labelColor: theme.color.mono[60], onClick: () => {
                                                handleDisplay(false);
                                                handleOnLogout();
                                            } })] })) })) : (jsxs(Buttons, { children: [jsx(ExButton, { variant: 'contained', size: 'large', label: "\uB85C\uADF8\uC778", onClick: () => {
                                            handleDisplay(false);
                                            handleOnClickLogin();
                                        } }), jsx(ExButton, { variant: 'contained', size: 'medium', label: "\uB85C\uADF8\uC778", onClick: () => {
                                            handleDisplay(false);
                                            handleOnClickLogin();
                                        } }), jsxs("div", Object.assign({ css: css `
									padding: 0 0 0 4px;
									display: flex;
									align-items: center;
								` }, { children: [jsx("span", Object.assign({ css: theme => css `
										flex: 1;
										color: ${theme.color.mono[60]};
										${theme.font.Subtitle2}
									` }, { children: "1\uBD84 \uB9CC\uC5D0 \uD68C\uC6D0\uC774 \uB418\uC5B4\uBCF4\uC138\uC694." })), jsx(ExButton, { variant: 'contents', size: 'small', label: '회원가입', onClick: () => {
                                                    handleDisplay(false);
                                                    if (handleOnSignup)
                                                        handleOnSignup();
                                                } })] }))] })), jsx("div", { css: css `
							height: 1px;
							background-color: ${theme.color.mono[10]};
						` }), jsx(Nav$1, { children: filter(gnb, obj => {
                                    if (auth)
                                        return true;
                                    return !obj.needAuth;
                                }).map((menu, index) => {
                                    var _a;
                                    return (jsxs(Fragment$1, { children: [jsxs("div", Object.assign({ css: css `
											display: flex;
											align-items: center;
											padding: 16px;
											gap: 8px;
											cursor: pointer;
											> span:first-of-type {
												flex: 1;
												color: ${theme.color.mono[80]};
												${theme.font.Subtitle};
											}
											.xicon- {
												font-size: 24px;
												color: ${theme.color
                                                    .primary[60]};
											}
										`, role: "button", tabIndex: index, onClick: e => {
                                                    // handleDisplay(false);
                                                    handleOnClickGnb(e, menu);
                                                }, onKeyDown: e => {
                                                    // handleDisplay(false);
                                                    handleOnClickGnb(e, menu);
                                                } }, { children: [jsx("span", { children: menu.title }), menu.hasLnb && (jsx("span", Object.assign({ className: "xicon-" }, { children: index === gnbIndex
                                                            ? 'chevron_direction_down'
                                                            : 'chevron_direction_right' })))] })), index === gnbIndex && (jsx(LnbBox, { children: (_a = menu.lnbMenu) === null || _a === void 0 ? void 0 : _a.map((lnbSection, sectionIndex) => {
                                                    var _a;
                                                    const length = ((_a = menu.lnbMenu) === null || _a === void 0 ? void 0 : _a.length) ||
                                                        0;
                                                    return (jsxs(Fragment$1, { children: [jsxs(LnbSectionMenu, { children: [!isNil(lnbSection.sectionTitle) && (jsx("span", { children: lnbSection.sectionTitle })), lnbSection.lnb.map((lnb, lnbIndex) => {
                                                                        var _a;
                                                                        return (jsxs(LnbMenu, { children: [jsxs("div", { children: [jsx("span", Object.assign({ role: "presentation", onClick: () => handleOnClickLnbTitle(lnb), css: css `
																							cursor: pointer;
																						` }, { children: lnb.title })), jsx("div", Object.assign({ className: "hashtags" }, { children: (_a = lnb.hashtag) === null || _a === void 0 ? void 0 : _a.map((tag, tagIndex) => {
                                                                                                return (jsx(ExLabelTag, { label: tag, variant: "default" }, tagIndex));
                                                                                            }) }))] }), jsx("span", { children: lnb.description })] }, lnbIndex));
                                                                    })] }), sectionIndex !==
                                                                length - 1 && (jsx("div", { css: theme => css `
																			height: 1px;
																			background-color: ${theme
                                                                    .color
                                                                    .mono[10]};
																		` }))] }, sectionIndex));
                                                }) }))] }, index));
                                }) })] })] }))] })));
};
styled.div `
	display: flex;
	height: 100%;
	position: absolute;
	right: 0;
`;
const CloseButton = styled.div `
	display: flex;
	//height: fit-content;
	${props => props.theme.breakpoints.down('TABLET')} {
		> div:nth-of-type(1) {
			display: none;
		}
		padding: 24px 0;
	}
	${props => props.theme.breakpoints.up('TABLET')} {
		> div:nth-of-type(2) {
			display: none;
		}
		padding: 16px;
	}
`;
const Box = styled.div `
	width: 312px;
	display: flex;
	flex-direction: column;
	gap: 16px;
	z-index: 1001;
	background-color: ${props => props.theme.color.white};
	${props => props.theme.breakpoints.down('TABLET')} {
		width: 312px;
		gap: 0;
	}
`;
const Buttons = styled.div `
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 40px 16px 16px;
	> div:nth-of-type(2) {
		display: none;
	}
	${props => props.theme.breakpoints.down('TABLET')} {
		gap: 12px;
		padding: 32px 16px 16px;
		> div:nth-of-type(1) {
			display: none;
		}
		> div:nth-of-type(2) {
			display: flex;
		}
	}
`;
const Nav$1 = styled.ul `
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 16px;
	overflow: auto;
	::-webkit-scrollbar {
		display: none;
	}
	${props => props.theme.breakpoints.down('TABLET')} {
		gap: 8px;
	}
`;
const Member$1 = styled.div `
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 40px 16px 16px;
	${props => props.theme.breakpoints.down('TABLET')} {
		padding: 32px 16px 16px 16px;
		gap: 12px;
	}
`;
const LnbBox = styled.div `
	display: flex;
	flex-direction: column;
	gap: 24px;
	padding: 24px;
	background-color: ${props => props.theme.color.primary[5]};
	border-radius: 8px;
	${props => props.theme.breakpoints.down('TABLET')} {
		padding: 16px;
	}
`;
const LnbSectionMenu = styled.div `
	display: flex;
	flex-direction: column;
	gap: 24px;
	padding: 0;
	> span {
		color: ${props => props.theme.color.mono[60]};
		${props => props.theme.font.Body2};
	}
	${props => props.theme.breakpoints.down('TABLET')} {
		gap: 16px;
	}
`;
const LnbMenu = styled.li `
	display: flex;
	flex-direction: column;
	gap: 4px;
	> div {
		display: flex;
		gap: 8px;
		> span {
			${props => props.theme.font.Subtitle};
			${props => props.theme.breakpoints.down('TABLET')} {
				${props => props.theme.font.Body2};
			}
		}
		> div {
			display: flex;
			${props => props.theme.breakpoints.down('TABLET')} {
				display: none;
			}
			gap: 8px;
		}
	}
	> span {
		color: ${props => props.theme.color.mono[40]};
		${props => props.theme.font.Caption};
	}
`;

const HeaderMaxWidth = 1440;
const TabletPadding = 12;
const MobilePadding = 4;
const TooltipMenu = [
    { label: '로그아웃', key: 'logout' },
];
const Header = ({ logo, subLogo, serviceLogo, subDomain, auth, userInfo, gnb, onLogin, onLogout, onSignup, onGoHome, onChangePartner, onGoMyAccount, onGoPartnerService, isMain, subtitle, shownDivider = true, notificationCallback, isHttps, mainUrl, url, port, mobileTitle, }) => {
    var _a, _b;
    const theme = useTheme();
    const [gnbIndex, setGnbIndex] = useState(-1);
    const [lnbShown, setLnbShown] = useState(false);
    const [lnbPosition, setLnbPosition] = useState(0);
    const [mobileNavShown, setMobileNavShown] = useState(false);
    const [userMenuTooltipShown, setUserMenuTooltipShown] = useState(false);
    const navigate = useNavigate();
    const userToolTip = cloneDeep(TooltipMenu);
    if ((_a = userInfo.roles) === null || _a === void 0 ? void 0 : _a.includes('CORPORATION')) {
        if (userInfo.isPartner) ;
        // userToolTip.unshift({
        // 	label: !userInfo.isPartner
        // 		? '파트너 페이지로 전환'
        // 		: '기본 페이지로 전환',
        // 	key: 'mode',
        // });
    }
    const handleOnSignup = () => {
        if (!isNil(onSignup))
            onSignup();
    };
    const handleOnClickLogin = () => {
        if (!isNil(onLogin))
            onLogin();
    };
    const handleOnClickGoHome = () => {
        if (!isNil(onGoHome))
            onGoHome();
    };
    /**
     * (데스크탑) LNB 메뉴 표시 핸들링
     * @param show
     * @param gnbIndex
     * @param left
     */
    const handleDisplayLnb = (show, gnbIndex, left) => {
        setGnbIndex(gnbIndex);
        setLnbShown(show);
        setLnbPosition(left || 0);
    };
    /**
     * (모바일, 태블릿) 플로팅 메뉴 표시 핸들링
     * @param show
     */
    const handleMobileNav = show => {
        setMobileNavShown(show);
    };
    const handleOnGnbClick = (gnb, event) => {
        if (gnb.hasLnb)
            return;
        navigate(`${gnb.path}`);
    };
    /**
     * (데스크탑) GNB 메뉴 마우스오버 이벤트
     * @param onGnb
     * @param event
     */
    const handleOnGnbOver = (onGnb, event) => {
        const { title } = onGnb;
        const gnbIndex = findIndex(gnb, { title });
        const left = event.target.getBoundingClientRect()
            .left;
        handleDisplayLnb(true, gnbIndex, left);
    };
    /**
     * 로그인 사용자의 정보 표시 버튼 클릭 이벤트
     */
    const handleOnClickUserInfo = () => {
        setUserMenuTooltipShown(prevState => !prevState);
    };
    /**로고 클릭했을 때 callback함수 */
    const handleOnClickLogo = () => {
        if (subDomain && isHttps && url && port) {
            window.location.href = getHref(isHttps, url, port);
        }
        else {
            navigate('/');
        }
    };
    const device = useResizeObserver();
    const handleOnClickTooltipMenu = ({ key }) => {
        switch (key) {
            case 'account':
                if (!isNil(onGoMyAccount))
                    onGoMyAccount();
                break;
            case 'logout':
                if (!isNil(onLogout))
                    onLogout();
                break;
            case 'mode':
                if (!isNil(onChangePartner))
                    onChangePartner();
                break;
            case 'service':
                if (!isNil(onGoPartnerService))
                    onGoPartnerService();
                break;
            default:
                setUserMenuTooltipShown(prevState => !prevState);
        }
    };
    useEffect(() => {
        if (mobileNavShown) {
            document.body.style.overflow = 'hidden';
        }
        else {
            document.body.style.overflow = 'unset';
        }
    }, [mobileNavShown]);
    return (jsxs(Wrapper, Object.assign({ isPartner: userInfo.isPartner && subDomain !== 'store' }, { children: [mobileTitle && device !== 'DESKTOP' && (jsx(MobileTitle, { children: mobileTitle })), jsxs("div", Object.assign({ css: theme => css `
					flex: 1;
					display: flex;
					gap: 24px;
					position: relative;
					${theme.breakpoints.down('DESKTOP')} {
						padding: 0 ${TabletPadding}px 0
							${TabletPadding + (isMain ? 12 : 0)}px;
					}
					${theme.breakpoints.down('TABLET')} {
						padding: 0 ${MobilePadding}px 0
							${MobilePadding + (isMain ? 12 : 0)}px;
					}
				` }, { children: [jsxs(Logo, Object.assign({ isMain: isMain, role: "img", onMouseEnter: () => handleDisplayLnb(false, -1) }, { children: [jsx("img", { role: "presentation", src: userInfo.isPartner && subDomain !== 'store'
                                    ? subLogo
                                    : logo, width: 130, alt: "\uC5D0\uB108\uC9C0\uC5D1\uC2A4 \uB85C\uACE0", onClick: () => {
                                    if (mainUrl) {
                                        window.location.href = mainUrl;
                                    }
                                    else {
                                        navigate('/');
                                    }
                                } }), !isNil(subDomain) && serviceLogo && (jsxs(Fragment, { children: [jsx("div", { css: theme => css `
									width: 1px;
									height: 18px;
									background-color: ${theme.color.mono[60]};
								` }), jsx("img", { src: serviceLogo, height: 18, alt: "", role: "presentation", onClick: handleOnClickLogo, style: {
                                            width: device === 'MOBILE' ? '48px' : '68px',
                                        } })] }))] })), jsxs(Nav, Object.assign({ isPartner: userInfo.isPartner }, { children: [subDomain !== 'store' &&
                                gnb.map((menu, index) => {
                                    return (jsxs("div", Object.assign({ role: "button", tabIndex: index, onKeyDown: e => handleOnGnbOver(menu, e), onClick: e => handleOnGnbClick(menu), onMouseOver: e => handleOnGnbOver(menu, e), onFocus: e => handleOnGnbOver(menu, e) }, { children: [jsx("div", { children: menu.title }), jsx("div", { css: theme => css `
												height: 3px;
												background-color: ${userInfo.isPartner
                                                    ? theme.color.white
                                                    : theme.color.primary[60]};
												opacity: ${index === gnbIndex
                                                    ? '100%'
                                                    : 0};
											` })] }), index));
                                }), jsx("div", { onMouseEnter: () => handleDisplayLnb(false, -1), css: css `
							flex: 1;
						` })] })), jsx(OtherNav, { children: subDomain === 'store' &&
                            filter(gnb, obj => {
                                if (obj.onlyMobile)
                                    return false;
                                if (auth)
                                    return true;
                                return !obj.needAuth;
                            }).map((menu, index) => {
                                return (jsx(ExButton, { variant: "contents", size: "small", label: menu.title, labelColor: theme.color.mono[80], onClick: e => handleOnGnbClick(menu) }, index));
                            }) }), !isMain && (jsxs(Fragment, { children: [jsx("div", Object.assign({ css: theme => css `
								flex: 1;
								display: flex;
								${theme.breakpoints.up('DESKTOP')} {
									display: none;
								}
								justify-content: flex-start;
								align-items: center;
							` }, { children: jsx(ExButton, { variant: 'contents', size: 'small', icon: 'chevron_direction_left', width: 36, onClick: () => history.back() }) })), jsx(Subtitle, { children: subtitle })] })), jsxs(MobileLayout, { children: [!isMain && (jsx(ExButton, { variant: 'contents', size: 'small', icon: 'mainpage_home', onClick: handleOnClickGoHome })), jsx(ExButton, { variant: 'contents', size: 'small', icon: 'menu', onClick: () => handleMobileNav(true) })] }), auth ? (jsxs(Member, Object.assign({ onMouseLeave: () => setUserMenuTooltipShown(false) }, { children: [subDomain !== 'store' && (jsx(NoticeButton, Object.assign({ isPartner: userInfo.isPartner }, { children: jsx("span", { children: "\uC54C\uB9BC\uD310 \uBC14\uB85C\uAC00\uAE30" }) }))), jsxs("div", { children: [jsx(ExButton, { variant: 'contents', size: 'small', label: userInfo.name, labelColor: userInfo.isPartner && subDomain !== 'store'
                                            ? '#fff'
                                            : undefined, icon: 'person_style_filled', iconPosition: "left", iconColor: userInfo.isPartner && subDomain !== 'store'
                                            ? '#fff'
                                            : undefined, backgroundColor: "inherit", onClick: handleOnClickUserInfo }), jsx(UserMenuTooltip, Object.assign({ show: userMenuTooltipShown }, { children: jsx("div", Object.assign({ css: theme => css `
										flex: 1;
										padding: 8px 0;
										background-color: ${theme.color
                                                .mono[90]};
										border-radius: 8px;
										> div {
											color: ${theme.color.white};
											${theme.font.Button_s};
										}
									` }, { children: userToolTip.map((menu, index) => {
                                                return (jsx("div", Object.assign({ role: "button", tabIndex: index, css: css `
													display: flex;
													padding: 8px 0;
													justify-content: center;
													cursor: pointer;
												`, onKeyDown: () => handleOnClickTooltipMenu(menu), onClick: () => handleOnClickTooltipMenu(menu) }, { children: menu.label }), index));
                                            }) })) }))] })] }))) : (jsxs(NonMember, { children: [jsx(ExButton, { variant: "bordered", size: "small", label: "\uD68C\uC6D0\uAC00\uC785", onClick: handleOnSignup }), jsx(ExButton, { variant: "contained", size: "small", label: "\uB85C\uADF8\uC778", onClick: handleOnClickLogin })] }))] })), jsx("div", { css: theme => css `
						width: 100%;
						height: 1px;
						background-color: ${theme.color.mono[10]};
						display: ${shownDivider ? 'block' : 'none'};
					` }), jsx(LnbMenu$1, { left: lnbPosition, display: lnbShown, lnbSection: (_b = gnb[gnbIndex]) === null || _b === void 0 ? void 0 : _b.lnbMenu, userInfo: userInfo, handleDisplay: handleDisplayLnb }), jsx(MobileNav, { display: mobileNavShown, gnb: gnb, auth: auth, userInfo: userInfo, handleOnClickLogin: handleOnClickLogin, onLogout: onLogout, handleDisplay: handleMobileNav, notificationCallback: notificationCallback, handleOnSignup: handleOnSignup })] })));
};
const Wrapper = styled.header `
	display: flex;
	flex-direction: column;
	position: sticky;
	top: 0;
	left: 0;
	right: 0;
	height: 72px;
	z-index: 999;
	padding: 0;
	@media (min-width: ${HeaderMaxWidth}px) {
		padding: 0 calc((100% - ${HeaderMaxWidth}px) / 2 + 24px);
	}
	${props => props.theme.breakpoints.up('DESKTOP')} {
		padding: 0 24px;
	}
	${props => props.theme.breakpoints.down('DESKTOP')} {
		padding: 0;
		height: 57px;
	}
	@media (min-width: ${HeaderMaxWidth}px) {
		padding: 0 calc((100% - ${HeaderMaxWidth}px) / 2 + 24px);
	}
	background-color: ${props => props.isPartner
    ? props.theme.color.primary[60]
    : props.theme.color.white};
`;
const MobileTitle = styled.div `
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	${({ theme }) => theme.font.Body_h};
`;
const Logo = styled.div `
	display: flex;
	align-items: center;
	gap: 12px;
	${props => props.theme.breakpoints.down('TABLET')} {
		gap: 8px;
		> img {
			width: 98px;
		}
	}
	${props => props.theme.breakpoints.down('DESKTOP')} {
		${props => (!props.isMain ? 'display: none' : '')}
	}
	img {
		cursor: pointer;
	}
`;
const Nav = styled.nav `
	flex: 1;
	display: flex;
	${props => props.theme.breakpoints.down('DESKTOP')} {
		display: none;
	}
	align-items: center;
	justify-content: start;
	gap: 16px;
	> div {
		height: 100%;
		display: flex;
		flex-direction: column;
		color: ${props => props.isPartner
    ? props.theme.color.white
    : props.theme.color.mono[90]};
		${props => props.theme.font.Subtitle};
		/* cursor: pointer; */
		> div:first-of-type {
			flex: 1;
			display: flex;
			align-items: center;
			padding: 8px 16px;
		}
	}
`;
const OtherNav = styled.nav `
	display: flex;
	gap: 8px;
	align-items: center;
	${props => props.theme.breakpoints.down('DESKTOP')} {
		display: none;
	}
`;
const Subtitle = styled.div `
	display: flex;
	${props => props.theme.breakpoints.up('DESKTOP')} {
		display: none;
	}
	align-items: center;
	color: ${props => props.theme.color.mono[90]};
	${props => props.theme.font.Title2_h};
`;
const NonMember = styled.div `
	display: flex;
	${props => props.theme.breakpoints.down('DESKTOP')} {
		display: none;
	}
	align-items: center;
	gap: 24px;
`;
const Member = styled.div `
	display: flex;
	${props => props.theme.breakpoints.down('DESKTOP')} {
		display: none;
	}
	align-items: center;
	gap: 24px;
	> div:last-of-type {
		display: flex;
		gap: 16px;
	}
`;
const NoticeButton = styled.div `
	display: flex;
	align-items: center;
	justify-content: center;
	width: 118px;
	height: 36px;
	border: 1px solid
		${props => props.isPartner
    ? props.theme.color.white
    : props.theme.color.primary[60]};
	border-radius: 100px;
	color: ${props => props.isPartner
    ? props.theme.color.white
    : props.theme.color.primary[60]};
	${props => props.theme.font.Button_s};
	cursor: pointer;
`;
const MobileLayout = styled.div `
	flex: 1;
	display: flex;
	${props => props.theme.breakpoints.up('DESKTOP')} {
		display: none;
	}
	justify-content: flex-end;
	align-items: center;
`;
const UserMenuTooltip = styled.div `
	display: ${props => (props.show ? 'flex' : 'none')};
	position: absolute;
	top: calc(50% + 50px);
	right: 0;
	width: 146px;
	::before {
		position: absolute;
		content: '';
		width: 100%;
		background: transparent;
		height: 24px;
		bottom: 100%;
	}
	::after {
		position: absolute;
		content: '';
		top: -24px;
		left: calc(50% - 12px);
		border-style: solid;
		border-width: 12px;
		border-color: transparent transparent
			${props => props.theme.color.mono[90]} transparent;
	}
`;

/* eslint-disable no-useless-escape */
const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const PASSWORD_REGEX = /^.*(?=^.{6,}$)(?=.*\d)(?=.*[a-zA-Z]).*$/;
const NAME_REGEX = /^[가-힣]+$/;
const NUMBER_REGEX = /^[0-9]*$/;
const FLOAT_REGEX = /[+-]?([0-9]*[.])?[0-9]+/;
const INTEGER_OR_FLOAT_REGEX = /^[0-9]*[.]{0,1}[0-9]*$/;
const SPACING_REGEX = /\s/g;
const PHONE_NUMBER_REGEX = /^01([0|1|6|7|8|9])-?([0-9]{3,4})-?([0-9]{4})$/;
const DECIMAL_POINT_REGEX = /^[-]?\d+(?:[.]\d+)?$/;
const IMAGE_TYPE = ['jpg', 'jpeg', 'png', 'JPG', 'JPEG', 'PNG'];
const IMAGE_PDF_TYPE = [
    'pdf',
    'jpg',
    'jpeg',
    'png',
    'PDF',
    'JPG',
    'JPEG',
    'PNG',
];
const PDF_TYPE = ['pdf', 'PDF'];
const URL_REGEX = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
const URL_NOT_PROTOCOL_REGEX = /[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/;
const PROJECT_NAME_REGEX = /^[ㄱ-ㅎ|가-힣|a-z|A-Z|0-9|\s]+$/;

const RULES_REQUIERD = {
    required: {
        value: true,
        message: '필수 입력 항목입니다.',
    },
};
const RULES_PATTERN_PHONE_NUMBER = {
    pattern: {
        value: PHONE_NUMBER_REGEX,
        message: '휴대폰 번호를 확인해주세요.',
    },
};
const RULES_PATTERN_EMAIL = {
    pattern: {
        value: EMAIL_REGEX,
        message: '이메일 주소를 확인해주세요.',
    },
};
const RULES_PHONE_NUMBER_MAX_LENGTH = {
    maxLength: {
        value: 13,
        message: '',
    },
};
const RULES_PATTERN_PASSWORD = {
    pattern: {
        value: PASSWORD_REGEX,
        message: '영문과 숫자를 조합하여 6자 이상 입력해주세요.',
    },
};

const color = {
    black: '#000',
    white: '#fff',
    mono: {
        100: '#191819',
        90: '#2F2F30',
        80: '#454547',
        70: '#5C5B5E',
        60: '#727174',
        50: '#88878B',
        40: '#9F9DA1',
        30: '#B4B4B6',
        20: '#CDCCCF',
        10: '#EBEAEC',
        5: '#F6F6F6',
    },
    primary: {
        100: '#1A0931',
        90: '#331360',
        80: '#4C1E8B',
        70: '#632AB3',
        60: '#7A37D8',
        50: '#8C5ECE',
        40: '#A183CB',
        30: '#B7A6CF',
        20: '#E5E1EC',
        10: '#F7F6F8',
        5: '#FAFAFA',
    },
    secondary: {
        100: '#032C29',
        90: '#0C5450',
        80: '#1C7B75',
        70: '#329E97',
        60: '#4DBFB8',
        50: '#76C5C0',
        40: '#9ACDCA',
        30: '#BAD7D5',
        20: '#D5E3E2',
        10: '#EDF0F0',
        5: '#F9FAFA',
    },
    red: {
        100: '#B22C3C',
        80: '#D93649',
        60: '#FF3F56',
        40: '#FF7989',
        20: '#FF9FAA',
        10: '#FFF9FA',
    },
    green: {
        100: '#369165',
        80: '#41B07A',
        60: '#4DCF90',
        40: '#82DDB1',
        20: '#A6E7C7',
        10: '#FAFEFC',
    },
    yellow: {
        100: '#B27B2D',
        80: '#D99636',
        60: '#FFB040',
        40: '#FFC879',
        20: '#FFD79F',
        10: '#FFFDF9',
    },
    blue: {
        100: '#2D62B2',
        80: '#3677D9',
        60: '#408CFF',
        40: '#79AEFF',
        20: '#9FC5FF',
        10: '#F9FCFF',
    },
};
const font = {
    H1: {
        fontSize: '38px',
        fontWeight: 700,
        lineHeight: '54px',
        letterSpacing: '-0.5px',
    },
    H2: {
        fontSize: '30px',
        fontWeight: 700,
        lineHeight: '46px',
        letterSpacing: '-0.5px',
    },
    Body2: {
        fontSize: '13px',
        fontWeight: 500,
        lineHeight: '18px',
        letterSpacing: '0px',
    },
    Body3_h: {
        fontSize: '22px',
        fontWeight: 700,
        lineHeight: '34px',
        letterSpacing: '-0.2px',
    },
    Body3: {
        fontSize: '22px',
        fontWeight: 400,
        lineHeight: '34px',
        letterSpacing: '-0.2px',
    },
    Title1_h: {
        fontSize: '20px',
        fontWeight: 700,
        lineHeight: '32px',
        letterSpacing: '0px',
    },
    Title2_h: {
        fontSize: '18px',
        fontWeight: 700,
        lineHeight: '28px',
        letterSpacing: '-0.2px',
    },
    Title: {
        fontSize: '18px',
        fontWeight: 500,
        lineHeight: '28px',
        letterSpacing: '-0.2px',
    },
    Subtitle: {
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: '24px',
        letterSpacing: '0px',
    },
    Body_h: {
        fontSize: '16px',
        fontWeight: 700,
        lineHeight: '24px',
        letterSpacing: '0px',
    },
    Body1: {
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: '24px',
        letterSpacing: '-0.1px',
    },
    Subtitle2_h: {
        fontSize: '14px',
        fontWeight: 700,
        lineHeight: '20px',
        letterSpacing: '0px',
    },
    Subtitle2: {
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '20px',
        letterSpacing: '0px',
    },
    Button_xl: {
        fontSize: '18px',
        fontWeight: 500,
        lineHeight: '28px',
        letterSpacing: '-0.25px',
    },
    Button_l: {
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: '28px',
        letterSpacing: '0',
    },
    Button_m: {
        fontSize: '14px',
        fontWeight: 700,
        lineHeight: '20px',
        letterSpacing: '0.2px',
    },
    Button_s: {
        fontSize: '13px',
        fontWeight: 500,
        lineHeight: '20px',
        letterSpacing: '-0.2px',
    },
    Button_s_h: {
        fontSize: '13px',
        fontWeight: 700,
        lineHeight: '20px',
        letterSpacing: '0.1px',
    },
    Caption_h: {
        fontSize: '12px',
        fontWeight: 700,
        lineHeight: '18px',
        letterSpacing: '0px',
    },
    Caption: {
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: '18px',
        letterSpacing: '0px',
    },
    Overline_h: {
        fontSize: '10px',
        fontWeight: 700,
        lineHeight: '14px',
        letterSpacing: '0.1px',
    },
    Overline: {
        fontSize: '10px',
        fontWeight: 400,
        lineHeight: '14px',
        letterSpacing: '0px',
    },
};
const elevation = {
    elevation1: {
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.24)',
    },
    elevation2: {
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.16)',
    },
    elevation3: {
        boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
    },
    elevation4: {
        boxShadow: '0px 16px 24px rgba(0, 0, 0, 0.1)',
    },
    elevation5: {
        boxShadow: '0px 16px 24px rgba(0, 0, 0, 0.1)',
    },
};
const breakpoint = {
    DESKTOP: '1200px',
    TABLET: '768px',
    MOBILE: '360px',
};
const breakpoints = {
    down: (_maxBreakpoint) => {
        const max = toNumber(breakpoint[_maxBreakpoint].replace('px', ''));
        return `@media (max-width: ${max - 0.5}px)`;
    },
    up: (_minBreakpoint) => {
        const min = breakpoint[_minBreakpoint];
        return `@media (min-width: ${min})`;
    },
    between: (_minBreakpoint, _maxBreakpoint) => {
        const min = breakpoint[_minBreakpoint];
        const max = toNumber(breakpoint[_maxBreakpoint || _minBreakpoint].replace('px', ''));
        return `@media (min-width: ${min}) and (max-width: ${max - 0.5}px)`;
    },
};
const theme = {
    color,
    font,
    elevation,
    breakpoint,
    breakpoints,
};

export { DECIMAL_POINT_REGEX, DialogContext, DialogContextProvider, EMAIL_REGEX, ExBottomSheet, ExButton, ExCheck, ExCheckUncontrolled, ExHashTag, ExInput, ExLabel, ExLabelStatus, ExLabelTag, ExPagination, ExRadio, ExRadioGroup, ExSelect, ExStepper, ExTabs, ExToast, ExToggle, ExTooltip, FLOAT_REGEX, Footer, Header, IMAGE_PDF_TYPE, IMAGE_TYPE, INTEGER_OR_FLOAT_REGEX, LnbMenu$1 as LNBMenu, MobileNav, Modal, NAME_REGEX, NUMBER_REGEX, PASSWORD_REGEX, PDF_TYPE, PHONE_NUMBER_REGEX, PROJECT_NAME_REGEX, Popup, RULES_PATTERN_EMAIL, RULES_PATTERN_PASSWORD, RULES_PATTERN_PHONE_NUMBER, RULES_PHONE_NUMBER_MAX_LENGTH, RULES_REQUIERD, SPACING_REGEX, TIMER_STATUS, URL_NOT_PROTOCOL_REGEX, URL_REGEX, callbackTimerTermination, theme as default, useResizeObserver, useToastMessage };
//# sourceMappingURL=index.js.map
